import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaUpload, FaEdit, FaSave, FaUndo, FaTag, FaUser, FaImage, FaPlus, FaTrash, FaBook, FaShare, FaArrowRight, FaArrowLeft, FaEye, FaCrop, FaCheck, FaCompress, FaExpand, FaRedo } from 'react-icons/fa';
import { auth, storage, db } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, setDoc, updateDoc, collection, getDocs, query, where, getDoc, addDoc } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import BackButton from '../components/BackButton';
import CharacterManager from '../components/CharacterManager';
import TagPositioningModal from '../components/TagPositioningModal';
import ReimagineModal from '../components/ReimagineModal';
import { API_ENDPOINTS, backendConfig } from '../config/backendConfig';
import homepageBackground from '../assets/Homepageback.png';
import splashImage from '../assets/splash.png';
import CharacterTagOverlay from '../components/CharacterTagOverlay';
import StoryboardingCropper from '../components/StoryboardingCropper';
import StorySetupModal from '../components/StorySetupModal';
import AddCharacterModal from '../components/AddCharacterModal';
import EnhancedCharacterModal from '../components/EnhancedCharacterModal';
import CharacterPreviewModal from '../components/CharacterPreviewModal';
import SceneEditModal from '../components/SceneEditModal';
import CustomModal from '../components/CustomModal';
import { checkContentModeration, getContentModerationMessage } from '../utils/contentModeration';
import ContentModerationModal from '../components/ContentModerationModal';
import { useFeatureQuota } from '../hooks/useFeatureQuota';
import UpgradeQuotaModal from '../components/UpgradeQuotaModal';
import { warmResolvedImageUrls } from '../utils/urlResolver';
import ArtmospherePremiumBackground, { atmospherePremiumPageStyle } from '../ui/backgrounds/ArtmospherePremiumBackground';

export default function StoryboardingStudio() {
  const navigate = useNavigate();
  const location = useLocation();
  const { checkQuotaBeforeUse, recordUsage, showUpgradeModal, dismissUpgradeModal, upgradeModalFeature } = useFeatureQuota();
  // Initialize step based on presence of storyId/editScene in the URL to avoid flicker of story setup
  const [currentStep, setCurrentStep] = useState(() => {
    try {
      const params = new URLSearchParams(location.search);
      const qStoryId = params.get('storyId');
      const editScene = params.get('editScene');
      if (qStoryId || editScene) return 'conceptualization';
    } catch (_) {}
    return 'story-setup';
  }); // story-setup, conceptualization, refining, scene-view
  const [currentScene, setCurrentScene] = useState(1);
  const [scenes, setScenes] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [selectedCharacters, setSelectedCharacters] = useState([]);
  const [sketchFile, setSketchFile] = useState(null);
  const [sketchUrl, setSketchUrl] = useState(null);
  const [refinedImageUrl, setRefinedImageUrl] = useState(null);
  const [sceneDescription, setSceneDescription] = useState('');
  const [sceneBrief, setSceneBrief] = useState('');
  const [characterDetails, setCharacterDetails] = useState('');
  const [sceneTags, setSceneTags] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showCharacterManager, setShowCharacterManager] = useState(false);
  const [showTagPositioning, setShowTagPositioning] = useState(false);
  const [showReimagine, setShowReimagine] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [editHistory, setEditHistory] = useState([]);
  const [currentEditIndex, setCurrentEditIndex] = useState(-1);
  const [storyTitle, setStoryTitle] = useState('');
  const [showCropper, setShowCropper] = useState(false);
  const [artworkCharacters, setArtworkCharacters] = useState([{ name: '', age: '', gender: '', appearance: '' }]);
  const [artworkType, setArtworkType] = useState('people');
  const [artworkObject, setArtworkObject] = useState({ name: '', brief: '' });

  const [objects, setObjects] = useState([]);
  const [showAddAnotherObject, setShowAddAnotherObject] = useState(false);
  // New: action bindings editor state (optional simple structure)
  const [actionBindings, setActionBindings] = useState([]); // [{actorTagId, objectTagId, action, relation, grip}]
  const [bindingDraft, setBindingDraft] = useState({
    actorTagId: '',
    objectTagId: '',
    action: '',
    relation: 'holding',
    grip: ''
  });
  // Scene/Object Library modal state
  const [showSceneObjectLibrary, setShowSceneObjectLibrary] = useState(false);
  const [showActionBindingModal, setShowActionBindingModal] = useState(false);
  const [pendingObjectTagId, setPendingObjectTagId] = useState(null);
  
  // Derived helpers for bindings
  const characterTagsForBindings = useMemo(
    () => (sceneTags || []).filter(tag => tag.characterData),
    [sceneTags]
  );
  const objectTagsForBindings = useMemo(
    () => (sceneTags || []).filter(tag => tag.objectData),
    [sceneTags]
  );
  
  // Derive a per-story object library from previously saved scenes' tags
  const sceneObjectLibrary = useMemo(() => {
    const uniqueByName = new Map();
    (scenes || []).forEach(scene => {
      (scene?.tags || []).forEach(tag => {
        const isObject = !!tag?.objectData || tag?.type === 'object';
        const name = tag?.objectData?.name || tag?.name;
        if (!isObject || !name) return;
        if (!uniqueByName.has(name)) {
          uniqueByName.set(name, {
            name,
            description: tag?.objectData?.description || tag?.description || '',
            x: typeof tag?.x === 'number' ? tag.x : (tag?.position?.x ?? 50),
            y: typeof tag?.y === 'number' ? tag.y : (tag?.position?.y ?? 50),
            sourceSceneNumber: scene?.sceneNumber || null
          });
        }
      });
    });
    return Array.from(uniqueByName.values());
  }, [scenes]);
  
  // Debounced artwork object name to prevent excessive tag generation
  const [debouncedArtworkObjectName, setDebouncedArtworkObjectName] = useState('');
  
  // Debounce the artwork object name
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedArtworkObjectName(artworkObject.name);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [artworkObject.name]);
  
  // DISABLED: Auto-create tags for scene items - now only manual creation via "Add Scene Item" button
  // This prevents the duplicate tag issue when typing
  /*
  React.useEffect(() => {
    if (artworkType === 'object' && debouncedArtworkObjectName.trim()) {
      // Check if this object has already been manually added
      const isManuallyAdded = objects.some(obj => obj.name === debouncedArtworkObjectName.trim());
      
      if (!isManuallyAdded) {
        setSceneTags(prev => {
          // Check if object tag already exists - improved duplicate detection
          const existingObjectTag = prev.find(tag => 
            tag.objectData && 
            tag.objectData.name === debouncedArtworkObjectName.trim()
          );
          
          if (!existingObjectTag) {
            const newTag = {
              id: uuidv4(),
              name: debouncedArtworkObjectName.trim(),
              x: 50, // Center position
              y: 50,
              objectData: {
                name: debouncedArtworkObjectName.trim(),
                type: 'object',
                description: artworkObject.brief || ''
              }
            };
            console.log('Auto-created object tag:', newTag);
            return [...prev, newTag];
          } else {
            // Update existing tag with new description if it changed
            if (existingObjectTag.objectData.description !== artworkObject.brief) {
              console.log('Updated existing object tag description:', debouncedArtworkObjectName);
              return prev.map(tag => 
                tag.id === existingObjectTag.id 
                  ? {
                      ...tag,
                      objectData: {
                        ...tag.objectData,
                        description: artworkObject.brief || ''
                      }
                    }
                  : tag
              );
            }
            return prev; // No changes needed
          }
        });
      }
    }
  }, [debouncedArtworkObjectName, artworkObject.brief, artworkType, objects]);
  */
  
  const [storySetupData, setStorySetupData] = useState(null);
  const [showAddCharacterModal, setShowAddCharacterModal] = useState(false);
  const [showEnhancedCharacterModal, setShowEnhancedCharacterModal] = useState(false);
  const [showCharacterPreviewModal, setShowCharacterPreviewModal] = useState(false);
  const [newCharacters, setNewCharacters] = useState([]);
  const [currentCharacterForPreview, setCurrentCharacterForPreview] = useState(null);
  const [currentCharacterImage, setCurrentCharacterImage] = useState(null);
  
  // Scene edit modal state
  const [showSceneEditModal, setShowSceneEditModal] = useState(false);
  const [currentSceneForEdit, setCurrentSceneForEdit] = useState(null);
  const [originalSceneData, setOriginalSceneData] = useState(null); // Add state for original scene data
  
  const [sceneGenre, setSceneGenre] = useState('');
  const [sceneMood, setSceneMood] = useState('');

  
  // Enhanced Scene Conceptualization Flow State
  const [conceptualizationStep, setConceptualizationStep] = useState(1); // 1: Scene Description & Sketch Upload, 2: Character Details & Tagging, 3: Validation & Confirmation
  const [sceneConceptualizationInProgress, setSceneConceptualizationInProgress] = useState(false);
  const [sceneConceptualizationAbortController, setSceneConceptualizationAbortController] = useState(null);
  const [showTagPlacementConfirmation, setShowTagPlacementConfirmation] = useState(false);
  const [showSceneConceptualizationLoader, setShowSceneConceptualizationLoader] = useState(false);
  const [sceneImageLoading, setSceneImageLoading] = useState(false);
  const [showDeleteSketchConfirm, setShowDeleteSketchConfirm] = useState(false);
  const [showUploadArtworkModal, setShowUploadArtworkModal] = useState(false);
  const [showBFLDetailsModal, setShowBFLDetailsModal] = useState(false);
  const [bflDetails, setBflDetails] = useState(null); // { prompt, inputImage, controlImage, inputImageUrl, aspectRatio }
  const [showContentModerationModal, setShowContentModerationModal] = useState(false);
  const [contentModerationMessage, setContentModerationMessage] = useState('');
  const [contentModerationKeywords, setContentModerationKeywords] = useState([]);
  const artworkUploadRef = useRef(null);
  
  // Story saving state
  const [isSavingStory, setIsSavingStory] = useState(false);
  const [storySaved, setStorySaved] = useState(false);
  const [storyId, setStoryId] = useState(null);
  
  // Image update tracking
  const [imageUpdateCounter, setImageUpdateCounter] = useState(0);
  useEffect(() => {
    const sceneUrls = (scenes || []).flatMap((scene) => [
      scene?.imageUrl,
      scene?.originalImageUrl,
      scene?.refinedImageUrl,
      scene?.editedImageUrl
    ]);
    warmResolvedImageUrls(
      [
        ...sceneUrls,
        sketchUrl,
        refinedImageUrl,
        currentCharacterImage
      ].filter(Boolean),
      40
    ).catch(() => {});
  }, [scenes, sketchUrl, refinedImageUrl, currentCharacterImage, imageUpdateCounter]);
  
  // Expanded artwork view state
  const [isArtworkExpanded, setIsArtworkExpanded] = useState(false);
  
  // Confirmation modal state
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [characterToDelete, setCharacterToDelete] = useState(null);
  
  // Save success modal state
  const [showSaveSuccessModal, setShowSaveSuccessModal] = useState(false);
  
  // Character library states
  const [showCharacterLibrary, setShowCharacterLibrary] = useState(false);
  const [userCharacters, setUserCharacters] = useState([]);
  const [selectedCharacterFromLibrary, setSelectedCharacterFromLibrary] = useState(null);
  const [characterConfirmed, setCharacterConfirmed] = useState(false);
  const [showAddAnotherCharacter, setShowAddAnotherCharacter] = useState(false);
  
  const fileInputRef = useRef(null);

  // Load existing story and characters on component mount
  useEffect(() => {
    // If storyId is passed via query, set it immediately
    try {
      const params = new URLSearchParams(location.search);
      const qStoryId = params.get('storyId');
      const editSceneParam = params.get('editScene');
      if (qStoryId && !storyId) {
        setStoryId(qStoryId);
      }
      if (editSceneParam) {
        const num = parseInt(editSceneParam, 10);
        if (!Number.isNaN(num) && num > 0) {
          setCurrentScene(num);
          setCurrentStep('conceptualization');
        }
      }
    } catch (_) {}
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setCurrentUser(user);
      if (user) {
        try {
          // Load existing story if storyId is provided AND it's from URL query (not just saved)
          // This prevents navigation when saving a new story
          const params = new URLSearchParams(location.search);
          const qStoryId = params.get('storyId');
          if (storyId && qStoryId && storyId === qStoryId) {
            // Only load if storyId matches URL query (meaning user navigated here with storyId)
            await loadExistingStory(storyId, user.uid);
          }
          // Try to fetch characters, but don't let it break the component
          try {
            await loadUserCharacterLibrary();
          } catch (error) {
            console.log('Character fetching failed, continuing without character data:', error.message);
          }
        } catch (error) {
          console.error('Error loading story data:', error);
          navigate('/storyboarding-landing');
        }
      } else {
        navigate('/storyboarding-landing');
      }
    });

    return () => unsubscribe();
  }, [storyId, navigate, location.search]);

  // Automatically generate tags for all characters in storySetupData
  useEffect(() => {
    if (storySetupData && storySetupData.characters && storySetupData.characters.length > 0 && conceptualizationStep === 3) {
      console.log('=== AUTOMATIC CHARACTER TAG GENERATION ===');
      console.log('storySetupData.characters:', storySetupData.characters.map(c => c.name));
      console.log('existing sceneTags:', sceneTags.map(tag => tag.name));
      
      // Check if characters were already transferred from story setup
      const storySetupCharacterNames = storySetupData.characters.map(char => char.name);
      const existingCharacterNames = sceneTags.map(tag => tag.name);
      
      console.log('storySetupCharacterNames:', storySetupCharacterNames);
      console.log('existingCharacterNames:', existingCharacterNames);
      
      // Only generate tags if there are characters in storySetupData that aren't already in sceneTags
      const charactersNeedingTags = storySetupData.characters.filter((char, idx) => 
        !existingCharacterNames.includes(char.name)
      );
      
      console.log('charactersNeedingTags:', charactersNeedingTags.map(c => c.name));
      
      if (charactersNeedingTags.length > 0) {
        const newTags = charactersNeedingTags.map((char, idx) => {
          const originalIndex = storySetupData.characters.indexOf(char);
          // Calculate position to spread tags across the artwork
          const totalCharacters = storySetupData.characters.length;
          const x = 20 + (originalIndex * (60 / Math.max(1, totalCharacters - 1)));
          const y = 20 + (originalIndex % 2) * 40;
          
          // Ensure character has an imageUrl
          const characterWithImage = ensureCharacterHasImageUrl(char);
          
          return {
            id: uuidv4(),
            name: characterWithImage.name,
            x: x,
            y: y,
            characterIndex: `story-${originalIndex}`,
            characterData: {
              ...characterWithImage,
              role: characterWithImage.role
            },
            role: characterWithImage.role
          };
        });
        
        setSceneTags(prev => [...prev, ...newTags]);
        console.log('Auto-generated tags for new characters:', newTags.map(tag => tag.name));
      } else {
        console.log('No new characters need tags - all characters already have tags');
      }
    }
  }, [storySetupData, conceptualizationStep]); // Added conceptualizationStep dependency

  // Load existing story data
  const loadExistingStory = async (storyId, userId) => {
    try {
      const storyRef = doc(db, 'stories', storyId);
      const storyDoc = await getDoc(storyRef);
      
      if (storyDoc.exists() && storyDoc.data().userId === userId) {
        const storyData = storyDoc.data();
        setStorySetupData(storyData.storySetupData);
        setStoryTitle(storyData.storyTitle);
        setScenes(storyData.scenes || []);
        setStoryId(storyId);
        setStorySaved(true);
        // Determine next scene number: last scene + 1 (min 1)
        const lastSceneNum = (storyData.scenes && storyData.scenes.length > 0)
          ? (storyData.scenes[storyData.scenes.length - 1].sceneNumber || storyData.scenes.length)
          : 0;
        // Only set currentScene if not already chosen from query param
        setCurrentScene(prev => {
          if (prev && prev > 0) return prev;
          return Math.max(1, lastSceneNum + 1);
        });
        setCurrentStep('conceptualization'); // Jump straight to conceptualization to add next scene
        
        console.log('Loaded existing story:', storyData);
      } else {
        console.error('Story not found or access denied');
        alert('Story not found or you don\'t have permission to access it.');
        navigate('/storyboarding-landing');
      }
    } catch (error) {
      console.error('Error loading story:', error);
      alert('Failed to load story. Please try again.');
      navigate('/storyboarding-landing');
    }
  };

  // Removed fetchCharacters function - using loadUserCharacterLibrary instead

  const handleTagPositionChange = useCallback((tagId, newX, newY) => {
    // Throttle logging to prevent excessive console output
    if (Math.random() < 0.01) { // Log only 1% of the time
      console.log('Tag position changed:', tagId, 'to:', { x: newX, y: newY });
    }
    setSceneTags(prev => prev.map(tag => 
      tag.id === tagId ? { ...tag, x: newX, y: newY } : tag
    ));
  }, []);

  const compressImage = (file, maxWidth = 1920, quality = 0.85) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to data URL with compression
          const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
          resolve(compressedDataUrl);
        };
        img.onerror = () => resolve(e.target.result); // Fallback to original if compression fails
        img.src = e.target.result;
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    });
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setSketchFile(file);
      
      // Compress image before converting to data URL to reduce payload size
      // This significantly reduces the JSON payload size (from 4.8MB to ~200-500KB)
      console.log('📸 Original file size:', (file.size / 1024 / 1024).toFixed(2), 'MB');
      const compressedDataUrl = await compressImage(file, 1920, 0.85);
      
      if (compressedDataUrl) {
        const compressedSize = (compressedDataUrl.length * 3 / 4 / 1024 / 1024).toFixed(2);
        console.log('📸 Compressed data URL size:', compressedSize, 'MB');
        setSketchUrl(compressedDataUrl);
      } else {
        // Fallback to original if compression fails
        const reader = new FileReader();
        reader.onload = (e) => {
          setSketchUrl(e.target.result);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleRefineScene = async () => {
    if (!sketchFile || !sceneDescription || !sceneBrief.trim()) {
      alert('Please upload a sketch, provide scene description, and enter a scene brief before proceeding.');
      return;
    }

    // Check if a character is selected for scene conceptualization
    if (sceneTags.length === 0 || !sceneTags[0]?.characterData?.imageUrl) {
      console.error('=== CHARACTER SELECTION ERROR ===');
      console.error('sceneTags length:', sceneTags.length);
      console.error('sceneTags content:', sceneTags);
      console.error('First tag characterData:', sceneTags[0]?.characterData);
      console.error('First tag imageUrl:', sceneTags[0]?.characterData?.imageUrl);
      
      // Additional debugging for character data structure
      if (sceneTags[0]?.characterData) {
        console.error('=== CHARACTER DATA DEBUG ===');
        console.error('All characterData fields:', Object.keys(sceneTags[0].characterData));
        console.error('Full characterData object:', JSON.stringify(sceneTags[0].characterData, null, 2));
        
        // Check if imageUrl exists in different possible locations
        const charData = sceneTags[0].characterData;
        console.error('imageUrl field:', charData.imageUrl);
        console.error('image field:', charData.image);
        console.error('url field:', charData.url);
        console.error('characterImage field:', charData.characterImage);
      }
      
      alert('Please select a character for scene conceptualization');
      return;
    }

    // Debug logging for character selection
    console.log('=== SCENE CONCEPTUALIZATION DEBUG ===');
    console.log('Scene Description:', sceneDescription);
    console.log('Scene Genre:', sceneGenre);
    console.log('Scene Mood:', sceneMood);
    
    console.log('Artwork Type:', artworkType);
    console.log('Artwork Object:', artworkObject);

    console.log('Scene Tags (Selected Characters):', sceneTags);
    console.log('Current Scene:', currentScene);
    console.log('Selected Characters Count:', sceneTags.length);
    
    // Log each selected character's details
    sceneTags.forEach((tag) => {
      console.log('Tag:', {
        name: tag.name,
        position: { x: tag.x, y: tag.y },
        characterData: tag.characterData,
        objectData: tag.objectData,
        surroundingData: tag.surroundingData,
        role: tag.role
      });
    });

    setIsLoading(true);
    try {
      // Debug the file being uploaded
      console.log('=== FILE UPLOAD DEBUG ===');
      console.log('Sketch file:', sketchFile);
      console.log('File name:', sketchFile.name);
      console.log('File type:', sketchFile.type);
      console.log('File size:', sketchFile.size);
      
      // Upload sketch directly to backend instead of Firebase Storage
      const formData = new FormData();
      formData.append('sketch', sketchFile);
      
      console.log('Uploading to:', API_ENDPOINTS.uploadSketch);
      const uploadResponse = await fetch(API_ENDPOINTS.uploadSketch, {
        method: 'POST',
        body: formData
      });
      
      console.log('Upload response status:', uploadResponse.status);
      console.log('Upload response headers:', Object.fromEntries(uploadResponse.headers.entries()));
      
      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        console.error('Upload error response:', errorText);
        throw new Error('Failed to upload sketch to backend');
      }
      
      const uploadResult = await uploadResponse.json();
      const sketchUrl = uploadResult.sketchUrl; // Backend already returns full URL

      // Get the selected character's image URL
      console.log('=== CHARACTER SELECTION DEBUG ===');
      console.log('sceneTags:', sceneTags);
      console.log('sceneTags[0]:', sceneTags[0]);
      console.log('sceneTags[0]?.characterData:', sceneTags[0]?.characterData);
      
      let selectedCharacterImageUrl = sceneTags[0]?.characterData?.imageUrl;
      console.log('Original selectedCharacterImageUrl:', selectedCharacterImageUrl);
      console.log('selectedCharacterImageUrl type:', typeof selectedCharacterImageUrl);
      console.log('selectedCharacterImageUrl length:', selectedCharacterImageUrl?.length);
      console.log('Is Firebase Storage URL:', selectedCharacterImageUrl?.includes('firebasestorage'));
      
      // Firebase Storage URLs are now handled properly by the backend
      if (selectedCharacterImageUrl?.includes('firebasestorage')) {
        console.log('Firebase Storage URL detected, backend will handle it properly');
      }
      
      if (!selectedCharacterImageUrl) {
        console.error('No character image URL found in sceneTags');
        console.error('Available sceneTags:', JSON.stringify(sceneTags, null, 2));
        throw new Error('No character image URL found. Please ensure you have selected a character with an image.');
      }

      // Prepare scene data for new conceptualization endpoint
      const sceneData = {
        sketchUrl: sketchUrl,
        sceneDescription,
        selectedCharacterImageUrl,
        userId: currentUser.uid,
        storyId: storyId
      };

      console.log('=== SENDING TO NEW CONCEPTUALIZATION ENDPOINT ===');
      console.log('Scene data being sent:', JSON.stringify(sceneData, null, 2));
      console.log('API endpoint:', API_ENDPOINTS.conceptualizeScene);
      console.log('Request start time:', new Date().toISOString());

      // Call new conceptualization API with extended timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.log('=== TIMEOUT TRIGGERED ===');
        console.log('Timeout occurred at:', new Date().toISOString());
        controller.abort();
      }, 120000); // 120 seconds timeout
      
      console.log('Making fetch request...');
      let response;
      try {
        response = await fetch(API_ENDPOINTS.conceptualizeScene, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(sceneData),
          signal: controller.signal
        });
        console.log('Fetch request completed successfully');
      } catch (fetchError) {
        console.error('Fetch request failed:', fetchError);
        clearTimeout(timeoutId);
        throw fetchError;
      }
      
      clearTimeout(timeoutId);
      console.log('Response received at:', new Date().toISOString());
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Backend response error:', errorText);
        throw new Error(`Failed to conceptualize scene: ${errorText}`);
      }

      const result = await response.json();
      
      console.log('=== BACKEND RESPONSE ===');
      console.log('Backend response:', result);
      console.log('Response success:', result.success);
      console.log('Response sceneImageUrl:', result.sceneImageUrl);
      console.log('Response message:', result.message);
      
      if (result.success) {
        // Use the sceneImageUrl directly (it's already a full Firebase Storage URL in production)
        // Only prepend backend URL if it's a relative path (local development)
        let fullSceneImageUrl = result.sceneImageUrl;
        
        console.log('Original result.sceneImageUrl:', result.sceneImageUrl);
        
        // Clean up URL if it has API endpoint incorrectly prepended
        if (fullSceneImageUrl && (fullSceneImageUrl.includes('cloudfunctions.net/apihttps://') || fullSceneImageUrl.includes('apihttps://'))) {
          // Extract the Firebase Storage URL part after the API endpoint
          // Pattern: ...apihttps://storage.googleapis.com/...
          const storageUrlMatch = fullSceneImageUrl.match(/api(https:\/\/[^\s\?]+)/);
          if (storageUrlMatch && storageUrlMatch[1]) {
            fullSceneImageUrl = storageUrlMatch[1];
            console.log('Cleaned malformed URL, extracted:', fullSceneImageUrl);
          } else {
            // Try alternative pattern: split at 'apihttps://'
            const splitIndex = fullSceneImageUrl.indexOf('apihttps://');
            if (splitIndex !== -1) {
              fullSceneImageUrl = fullSceneImageUrl.substring(splitIndex + 3); // +3 to skip 'api'
              console.log('Cleaned malformed URL using split, extracted:', fullSceneImageUrl);
            }
          }
        }
        
        // Check if URL is already complete (starts with http:// or https://)
        if (fullSceneImageUrl && (fullSceneImageUrl.startsWith('http://') || fullSceneImageUrl.startsWith('https://'))) {
          // Already a full URL (Firebase Storage URL) - check if cache-busting parameter already exists
          if (fullSceneImageUrl.includes('?t=')) {
            // Replace existing timestamp
            fullSceneImageUrl = fullSceneImageUrl.replace(/\?t=\d+/, `?t=${Date.now()}`);
          } else {
            // Add cache-busting parameter
            fullSceneImageUrl = `${fullSceneImageUrl}?t=${Date.now()}`;
          }
        } else if (fullSceneImageUrl && fullSceneImageUrl.startsWith('/uploads/')) {
          // Relative path - this should NOT happen in production (backend should return Firebase Storage URL)
          // But if it does, use backend proxy endpoint instead of localhost
          const isProduction = window.location.hostname !== 'localhost' && !window.location.hostname.includes('127.0.0.1');
          if (isProduction) {
            // In production, use backend proxy to fetch from Firebase Storage
            const backendBase = backendConfig.baseUrl || 'https://us-central1-artmosphere-89b64.cloudfunctions.net/api';
            fullSceneImageUrl = `${backendBase}/proxy-image?url=${encodeURIComponent(fullSceneImageUrl)}?t=${Date.now()}`;
            console.warn('⚠️ Backend returned relative path in production, using proxy:', fullSceneImageUrl);
          } else {
            // Local development - prepend backend URL
            fullSceneImageUrl = `${backendConfig.baseUrl}${result.sceneImageUrl}?t=${Date.now()}`;
          }
        } else if (fullSceneImageUrl) {
          // Relative path or other format - add cache-busting
          fullSceneImageUrl = `${result.sceneImageUrl}?t=${Date.now()}`;
        }
        
        console.log('Setting refinedImageUrl to:', fullSceneImageUrl);
        console.log('Previous refinedImageUrl was:', refinedImageUrl);
        setRefinedImageUrl(fullSceneImageUrl);
        
        // Automatically create a scene when image is generated
        if (fullSceneImageUrl && currentScene) {
          console.log('🎬 Automatically creating scene for current scene:', currentScene);
          const newScene = {
            id: `scene_${currentScene}_${Date.now()}`,
            sceneNumber: currentScene,
            sceneTitle: `Scene ${currentScene}`,
            sceneDescription: sceneDescription || `Scene ${currentScene} description`,
            refinedImageUrl: fullSceneImageUrl,
            imageUrl: fullSceneImageUrl,
            characterDetails: characterDetails,
            tags: sceneTags,
            genre: sceneGenre,
            mood: sceneMood,
            createdAt: new Date().toISOString()
          };
          
          console.log('🎬 Calling setScenes...');
          setScenes(prev => {
            console.log('🎬 Inside setScenes callback, previous scenes:', prev.length);
            // Replace existing scene or add new one
            const existingIndex = prev.findIndex(scene => scene.sceneNumber === currentScene);
            let updatedScenes;
            if (existingIndex >= 0) {
              updatedScenes = [...prev];
              updatedScenes[existingIndex] = newScene;
              console.log('🔄 Updated existing scene:', newScene);
            } else {
              updatedScenes = [...prev, newScene];
              console.log('➕ Added new scene:', newScene);
            }
            console.log('🎬 Returning from setScenes callback');
            return updatedScenes;
          });
          console.log('🎬 setScenes call completed');
          
          // Save to Firebase AFTER setScenes (non-blocking, fire and forget)
          if (storyId) {
            console.log('💾 Scheduling Firebase save (non-blocking)...');
            // Use setTimeout to ensure setScenes completes and doesn't block
            setTimeout(async () => {
              try {
                console.log('💾 Starting Firebase save...');
                const storyRef = doc(db, 'stories', storyId);
                const storyDoc = await getDoc(storyRef);
                const currentScenes = storyDoc.exists() ? storyDoc.data().scenes || [] : [];
                
                const existingIndex = currentScenes.findIndex(s => s.sceneNumber === currentScene);
                let updatedScenes;
                if (existingIndex >= 0) {
                  updatedScenes = [...currentScenes];
                  updatedScenes[existingIndex] = newScene;
                } else {
                  updatedScenes = [...currentScenes, newScene];
                }
                
                await updateDoc(storyRef, {
                  userId: currentUser.uid,
                  storyTitle,
                  storySetupData,
                  scenes: updatedScenes,
                  totalScenes: updatedScenes.length,
                  coverUrl: updatedScenes.length > 0 ? (updatedScenes[0]?.refinedImageUrl || updatedScenes[0]?.imageUrl || null) : null,
                  updatedAt: new Date(),
                  status: 'in-progress'
                });
                console.log('✅ Story auto-saved successfully with', updatedScenes.length, 'scenes');
              } catch (error) {
                console.error('❌ Auto-save failed:', error);
              }
            }, 0); // Execute on next tick
          }
        }
        
        // If this was triggered from Step 3, show loader and then move to scene view
        // Move to scene view after successful generation
        setCurrentStep('scene-view');
        // If triggered from Step 2, just store the result and continue to Step 3
      } else {
        throw new Error(result.error || 'Failed to conceptualize scene');
      }

    } catch (error) {
      console.error('Error conceptualizing scene:', error);
      if (error.name === 'AbortError') {
        alert('Scene conceptualization timed out. Please try again - this can take up to 2 minutes.');
      } else {
        alert('Failed to conceptualize scene. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleNextScene = () => {
    // Check if we've reached the 15 scene limit
    if (currentScene >= 15) {
      alert('You have reached the maximum limit of 15 scenes for this story.');
      return;
    }

    // Save current scene to scenes array if it exists
    if (refinedImageUrl && sceneDescription) {
      const newScene = {
        id: `scene_${currentScene}_${Date.now()}`,
        sceneNumber: currentScene,
        imageUrl: refinedImageUrl,
        description: sceneDescription,
        characterDetails: characterDetails,
        tags: sceneTags,
        genre: sceneGenre,
        mood: sceneMood,
        createdAt: new Date().toISOString()
      };
      
      setScenes(prev => {
        // Replace existing scene or add new one
        const existingIndex = prev.findIndex(scene => scene.sceneNumber === currentScene);
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = newScene;
          return updated;
        } else {
          return [...prev, newScene];
        }
      });
    }

    // Move to next scene
    setCurrentScene(prev => prev + 1);
    // Close any open modals when moving to next scene
    setShowUploadArtworkModal(false);
    setShowBFLDetailsModal(false);
    setCurrentStep('conceptualization');
    resetConceptualizationStep(); // Reset to first sub-step
    
    // Reset form for next scene
    setSketchFile(null);
    setSketchUrl(null);
    setRefinedImageUrl(null);
    setSceneDescription('');
    setCharacterDetails('');
    setSceneTags([]);
    setActionBindings([]); // Clear action bindings - each scene should have its own actions
    setArtworkType('people');
    setArtworkObject({ name: '', brief: '' });

    setSceneGenre('');
    setSceneMood('');
    
    // Clear any ongoing conceptualization state for the new scene
    setSceneConceptualizationInProgress(false);
    setShowSceneConceptualizationLoader(false);
    setSceneImageLoading(false);
    if (sceneConceptualizationAbortController) {
      sceneConceptualizationAbortController.abort();
      setSceneConceptualizationAbortController(null);
    }

  };

  const handleStorySetupComplete = async (data) => {
    console.log('=== STORY SETUP COMPLETE ===');
    console.log('Story setup data:', data);
    console.log('Characters in story setup:', data.characters);
    if (data.characters && data.characters.length > 0) {
      data.characters.forEach((char, idx) => {
        console.log(`Character ${idx + 1}:`, {
          name: char.name,
          hasImageUrl: !!char.imageUrl,
          imageUrl: char.imageUrl,
          allFields: char
        });
      });
    }
    
    setStorySetupData(data);
    setStoryTitle(data.storyTitle);
    setCurrentStep('conceptualization');
    resetConceptualizationStep(); // Reset to first sub-step
    
    // Load character library to ensure we have access to character images
    await loadUserCharacterLibrary();
    
    // Clear any existing sceneTags - users will add characters manually in Step 2
    console.log('=== CLEARING SCENE TAGS - NO DEFAULT CHARACTERS ===');
    setSceneTags([]); // Start with no characters - users must add them manually
    
    // Save character to Firebase if we have a character with image
    if (data.mainCharacterWithImage && currentUser) {
      try {
        // First, create the story to get a storyId
        const storyData = {
          userId: currentUser.uid,
          storyTitle: data.storyTitle,
          storySetupData: data,
          totalScenes: 0,
          createdAt: new Date(),
          updatedAt: new Date(),
          status: 'in-progress'
        };

        // Save story to Firestore
        const storyRef = collection(db, 'stories');
        const result = await addDoc(storyRef, storyData);
        const newStoryId = result.id;
        setStoryId(newStoryId);
        
        // Now save the character with the storyId
        const characterData = {
          ...data.mainCharacterWithImage,
          storyId: newStoryId,
          userId: currentUser.uid
        };

        const response = await fetch(`${API_ENDPOINTS.backendBase}/api/characters/create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            storyId: newStoryId,
            characterData: characterData
          })
        });

        if (!response.ok) {
          console.error('Failed to save character to Firebase');
        } else {
          console.log('Character saved to Firebase successfully');
        }
        
      } catch (error) {
        console.error('Error saving character to Firebase:', error);
        // Continue with the flow even if character save fails
      }
    }
    
    // Fetch previous scenes for character consistency
    fetchPreviousScenes(data.storyTitle);
  };

  // Handle back navigation - preserve story data
  const handleBackNavigation = () => {
    if (currentStep === 'conceptualization') {
      // If we have story setup data, go back to story setup
      if (storySetupData) {
        setCurrentStep('story-setup');
      } else {
        // Otherwise go to landing page
        navigate('/storyboarding-landing');
      }
    } else if (currentStep === 'scene-view') {
      // Go back to conceptualization
      // Close any open modals when navigating away from scene-view
      setShowUploadArtworkModal(false);
      setShowBFLDetailsModal(false);
      setCurrentStep('conceptualization');
    } else {
      // Default back to landing
      navigate('/storyboarding-landing');
    }
  };

  // Save story to Firebase
  const handleSaveStory = async () => {
    console.log('🔍 SAVE STORY DEBUG: Starting save process...');
    console.log('📊 Current State:', {
      storyTitle: storyTitle,
      hasStoryTitle: storyTitle && storyTitle.trim(),
      currentUser: currentUser ? 'Authenticated' : 'Not authenticated',
      userId: currentUser?.uid,
      scenesCount: scenes?.length || 0,
      hasStorySetupData: !!storySetupData,
      storyId: storyId
    });

    // Enhanced authentication check
    if (!currentUser) {
      console.log('❌ Save failed: currentUser is null');
      alert('Please log in to save your story.');
      return;
    }

    if (!currentUser.uid) {
      console.log('❌ Save failed: currentUser.uid is null');
      alert('Authentication error. Please log in again.');
      return;
    }

    // Double-check with Firebase auth
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      console.log('❌ Save failed: auth.currentUser is null');
      alert('Authentication error. Please log in again.');
      return;
    }

    if (firebaseUser.uid !== currentUser.uid) {
      console.log('❌ Save failed: UID mismatch between currentUser and auth.currentUser');
      console.log('   currentUser.uid:', currentUser.uid);
      console.log('   auth.currentUser.uid:', firebaseUser.uid);
      alert('Authentication error. Please log in again.');
      return;
    }

    if (!storyTitle.trim()) {
      console.log('❌ Save failed: No story title');
      alert('Please enter a story title');
      return;
    }

    setIsSavingStory(true);
    console.log('🔄 Save in progress...');
    
    try {
      // Validate and clean data before saving
      const cleanStoryTitle = storyTitle?.trim() || 'Untitled Story';
      const cleanStorySetupData = storySetupData || {};
      const cleanScenes = scenes || [];
      
      // Remove any undefined values from storySetupData
      const cleanedStorySetupData = Object.fromEntries(
        Object.entries(cleanStorySetupData).filter(([key, value]) => value !== undefined)
      );
      
      // Remove any undefined values from scenes
      const cleanedScenes = cleanScenes.map(scene => {
        if (!scene) return null;
        return Object.fromEntries(
          Object.entries(scene).filter(([key, value]) => value !== undefined)
        );
      }).filter(scene => scene !== null);
      
      const storyData = {
        userId: currentUser.uid, // Use the verified UID
        storyTitle: cleanStoryTitle,
        storySetupData: cleanedStorySetupData,
        scenes: cleanedScenes,
        totalScenes: cleanedScenes.length,
        // Set the first scene as the book cover if available
        coverUrl: cleanedScenes.length > 0 ? (cleanedScenes[0]?.refinedImageUrl || cleanedScenes[0]?.imageUrl || null) : null,
        createdAt: new Date(),
        updatedAt: new Date(),
        status: 'in-progress'
      };

      console.log('📦 Prepared story data:', {
        userId: storyData.userId,
        storyTitle: storyData.storyTitle,
        scenesCount: storyData.totalScenes,
        status: storyData.status,
        hasUserId: !!storyData.userId,
        userIdType: typeof storyData.userId
      });
      
      // Debug: Check for undefined values in storyData
      console.log('🔍 Checking for undefined values in storyData...');
      Object.entries(storyData).forEach(([key, value]) => {
        if (value === undefined) {
          console.error(`❌ Found undefined value in field: ${key}`);
        } else if (value === null) {
          console.warn(`⚠️ Found null value in field: ${key}`);
        } else if (typeof value === 'object' && value !== null) {
          // Check nested objects
          const checkNestedUndefined = (obj, path = '') => {
            Object.entries(obj).forEach(([nestedKey, nestedValue]) => {
              const fullPath = path ? `${path}.${nestedKey}` : nestedKey;
              if (nestedValue === undefined) {
                console.error(`❌ Found undefined value in nested field: ${fullPath}`);
              } else if (nestedValue === null) {
                console.warn(`⚠️ Found null value in nested field: ${fullPath}`);
              } else if (typeof nestedValue === 'object' && nestedValue !== null && !Array.isArray(nestedValue)) {
                checkNestedUndefined(nestedValue, fullPath);
              }
            });
          };
          checkNestedUndefined(value, key);
        }
      });

      // Save to Firestore
      const storyRef = storyId 
        ? doc(db, 'stories', storyId)
        : collection(db, 'stories');
      
      console.log('💾 Saving to Firestore...');
      console.log('   Operation:', storyId ? 'updateDoc' : 'addDoc');
      console.log('   Story ID:', storyId || 'New story');
      console.log('   User ID:', storyData.userId);
      
      const result = storyId 
        ? await updateDoc(storyRef, { ...storyData, updatedAt: new Date() })
        : await addDoc(storyRef, storyData);

      if (!storyId) {
        setStoryId(result.id);
        console.log('🆔 New story ID assigned:', result.id);
      }
      
      setStorySaved(true);
      console.log('✅ Story saved successfully:', result);
      
      // Show success modal instead of alert - NO NAVIGATION
      setShowSaveSuccessModal(true);
      
      // Ensure we stay on the current step and don't navigate
      // Do NOT change currentStep here - user should stay where they are
      
    } catch (error) {
      console.error('❌ Error saving story:', error);
      console.error('❌ Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      
      // Provide more specific error messages
      let errorMessage = 'Failed to save story. Please try again.';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please check your account permissions.';
        console.log('🔍 Permission denied - checking authentication state...');
        console.log('   currentUser:', currentUser);
        console.log('   auth.currentUser:', auth.currentUser);
        console.log('   currentUser.uid:', currentUser?.uid);
      } else if (error.code === 'unavailable') {
        errorMessage = 'Network error. Please check your internet connection.';
      } else if (error.code === 'auth/user-not-found') {
        errorMessage = 'Authentication error. Please log in again.';
      } else if (error.code === 'invalid-argument') {
        errorMessage = 'Invalid data. Please check your story information.';
      }
      
      alert(errorMessage);
    } finally {
      setIsSavingStory(false);
      console.log('🔄 Save process completed');
    }
  };

  // Publish story (mark as complete)
  const handlePublishStory = async () => {
    console.log('🔍 PUBLISH STORY DEBUG: Starting publish process...');
    console.log('📊 Current State:', {
      storySaved: storySaved,
      storyId: storyId,
      currentUser: currentUser ? 'Authenticated' : 'Not authenticated'
    });

    if (!storySaved) {
      console.log('❌ Publish failed: Story not saved');
      alert('Please save your story first before publishing.');
      return;
    }

    if (!storyId) {
      console.log('❌ Publish failed: No story ID');
      alert('Story not found. Please save your story first.');
      return;
    }

    if (!currentUser || !currentUser.uid) {
      console.log('❌ Publish failed: User not authenticated');
      alert('Please log in to publish your story.');
      return;
    }

    try {
      console.log('💾 Publishing story to Firestore...');
      console.log('   Story ID:', storyId);
      
      const storyRef = doc(db, 'stories', storyId);
      await updateDoc(storyRef, { 
        status: 'published',
        publishedAt: new Date(),
        updatedAt: new Date()
      });
      
      console.log('✅ Story published successfully');
      alert('Story published successfully! It\'s now available in My Art Stories.');
      
    } catch (error) {
      console.error('❌ Error publishing story:', error);
      console.error('❌ Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      
      // Provide more specific error messages
      let errorMessage = 'Failed to publish story. Please try again.';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please check your account permissions.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Network error. Please check your internet connection.';
      } else if (error.code === 'auth/user-not-found') {
        errorMessage = 'Authentication error. Please log in again.';
      } else if (error.code === 'not-found') {
        errorMessage = 'Story not found. Please save your story first.';
      }
      
      alert(errorMessage);
    }
  };

  const fetchPreviousScenes = async (storyTitle) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.getPreviousScenes}?userId=${currentUser.uid}&storyTitle=${encodeURIComponent(storyTitle)}`);
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.scenes.length > 0) {
          // Set the current scene number to the next available scene
          const maxSceneNumber = Math.max(...result.scenes.map(scene => scene.sceneNumber));
          setCurrentScene(maxSceneNumber + 1);
          
          // Load existing scenes
          setScenes(result.scenes.map(scene => ({
            id: scene.id,
            number: scene.sceneNumber,
            description: scene.sceneDescription,
            imageUrl: scene.refinedImageUrl,
            sketchUrl: scene.sketchUrl,
            tags: scene.sceneTags || [],
            artworkType: scene.artworkType,
            artworkObject: scene.artworkObject,
    
          })));
          
          console.log('Previous scenes loaded for character consistency:', result.scenes);
        }
      }
    } catch (error) {
      console.error('Error fetching previous scenes:', error);
    }
  };

  const handleAddNewCharacter = (character) => {
    setNewCharacters([...newCharacters, character]);
    // Add tag for the new character
    const totalNewCharacters = newCharacters.length + 1;
    const x = 15 + (totalNewCharacters * 20);
    const y = 15 + (totalNewCharacters % 2) * 30;
    
    const newTag = {
      id: uuidv4(),
      name: character.name,
      x: x,
      y: y,
      characterIndex: `new-${newCharacters.length}`,
      characterData: character,
      role: character.role
    };
    console.log('Adding new character tag:', character.name, 'at position:', { x, y });
    setSceneTags(prev => [...prev, newTag]);
  };

  const handleEnhancedCharacterAdded = (character) => {
    console.log('Adding enhanced character:', character);
    setNewCharacters(prev => [...prev, character]);
    setShowEnhancedCharacterModal(false);
    
    // Add tag for the new character
    const totalNewCharacters = newCharacters.length + 1;
    const x = 15 + (totalNewCharacters * 20);
    const y = 15 + (totalNewCharacters % 2) * 30;
    
    const newTag = {
      id: uuidv4(),
      name: character.name,
      x: x,
      y: y,
      characterIndex: `new-${newCharacters.length}`,
      characterData: character,
      role: character.role
    };
    console.log('Adding enhanced character tag:', character.name, 'at position:', { x, y });
    setSceneTags(prev => [...prev, newTag]);
  };

  const handleCharacterPreviewSave = (character) => {
    console.log('Saving character from preview:', character);
    setNewCharacters(prev => [...prev, character]);
    setShowCharacterPreviewModal(false);
    setCurrentCharacterForPreview(null);
    setCurrentCharacterImage(null);
  };

  const handleCharacterEdit = async (editPrompt) => {
    // This would typically call the BFL API to regenerate the image with the new prompt
    console.log('Editing character with prompt:', editPrompt);
    // For now, just show a success message
    alert('Character updated successfully!');
  };

  const handleCharacterRegenerate = async (prompt) => {
    // This would typically call the BFL API to regenerate the image
    console.log('Regenerating character with prompt:', prompt);
    // For now, just show a success message
    alert('Character image regenerated successfully!');
  };

  // Handle scene recreation with same parameters (disabled)
  const handleRecreateScene = async () => {
    console.log('Recreate feature is disabled.');
    return;
    console.log('=== RECREATE SCENE BUTTON CLICKED ===');
    console.log('Recreating scene with same parameters...');
    console.log('Current scene:', currentScene);
    console.log('Scene description:', sceneDescription);
    console.log('Scene brief:', sceneBrief);
    console.log('Scene tags:', sceneTags);
    console.log('Sketch URL:', sketchUrl);
    console.log('Sketch file:', sketchFile);
    
    // Find the saved scene data to get the original character tags used for this scene
    const savedScene = scenes.find(scene => scene.sceneNumber === currentScene);
    console.log('Saved scene data:', savedScene);
    console.log('Saved scene tags:', savedScene?.tags);
    
    // Use the original character tags from the saved scene (the ones actually used for generation)
    const originalSceneTags = savedScene?.tags || sceneTags;
    console.log('Using original scene tags for recreation:', originalSceneTags);
    
    // Validate required parameters
    if (!sketchUrl && !sketchFile) {
      alert('No sketch available for recreation. Please upload a sketch first.');
      return;
    }
    
    if (!sceneDescription || !sceneBrief.trim()) {
      alert('Scene description and brief are required for recreation.');
      return;
    }
    
    if (originalSceneTags.length === 0) {
      alert('No characters selected for recreation. Please select characters first.');
      return;
    }
    
    try {
      // Temporarily restore the original scene tags that were used for this scene
      const currentSceneTags = sceneTags;
      setSceneTags(originalSceneTags);
      
      // Reset the refined image URL to show loading state
      setRefinedImageUrl(null);
      
      // Start scene conceptualization with the original parameters (exact same process)
      await startSceneConceptualization(true);
      
      // Restore current scene tags after recreation
      setSceneTags(currentSceneTags);
      
      console.log('✅ Scene recreation initiated successfully with original character data');
    } catch (error) {
      console.error('❌ Scene recreation failed:', error);
      alert('Failed to recreate scene. Please try again.');
    }
  };

  // Scene edit handlers
  const handleSceneEdit = (scene) => {
    console.log('=== HANDLE SCENE EDIT CALLED ===');
    console.log('Scene data received:', scene);
    console.log('showSceneEditModal before:', showSceneEditModal);
    console.log('currentSceneForEdit before:', currentSceneForEdit);
    
    // If no scene is provided, create one from current state
    if (!scene) {
      const currentSceneData = {
        imageUrl: refinedImageUrl,
        title: `Scene ${currentScene}`,
        description: sceneDescription,
        sceneNumber: currentScene,
        genre: sceneGenre,
        mood: sceneMood,
        characterDetails: characterDetails,
        tags: sceneTags
      };
      console.log('Created current scene data:', currentSceneData);
      setCurrentSceneForEdit(currentSceneData);
      // Store original data for undo
      setOriginalSceneData({
        imageUrl: refinedImageUrl,
        title: `Scene ${currentScene}`,
        description: sceneDescription,
        sceneNumber: currentScene,
        genre: sceneGenre,
        mood: sceneMood,
        characterDetails: characterDetails,
        tags: sceneTags
      });
    } else {
      console.log('Using provided scene data:', scene);
      setCurrentSceneForEdit(scene);
      // Store original data for undo
      setOriginalSceneData({
        imageUrl: scene.imageUrl,
        title: scene.title,
        description: scene.description,
        sceneNumber: scene.sceneNumber,
        genre: scene.genre,
        mood: scene.mood,
        characterDetails: scene.characterDetails,
        tags: scene.tags
      });
    }
    console.log('Setting showSceneEditModal to true');
    setShowSceneEditModal(true);
    console.log('showSceneEditModal after setting:', true);
    console.log('currentSceneForEdit after setting:', scene || 'current scene data');
  };

  const handleSceneUpdate = (updatedScene) => {
    console.log('handleSceneUpdate called with:', updatedScene);
    console.log('Current scene number:', currentScene);
    console.log('Updated scene number:', updatedScene.sceneNumber);
    
    // Update the scene in the scenes array
    const updatedScenes = scenes.map(scene => 
      scene.sceneNumber === updatedScene.sceneNumber ? updatedScene : scene
    );
    setScenes(updatedScenes);
    
    // Update the main view if this is the current scene
    if (updatedScene.sceneNumber === currentScene) {
      console.log('Updating main view with new image URL:', updatedScene.imageUrl);
      setRefinedImageUrl(updatedScene.imageUrl);
      setSceneDescription(updatedScene.description || '');
      setCharacterDetails(updatedScene.characterDetails);
      setSceneTags(updatedScene.tags || []);
      setSceneGenre(updatedScene.genre || '');
      setSceneMood(updatedScene.mood || '');

      // Force image refresh
      setImageUpdateCounter(prev => prev + 1);
    }
    
    setShowSceneEditModal(false);
    setCurrentSceneForEdit(null);
    setOriginalSceneData(null); // Clear original data after successful update
  };

  // Undo function to revert scene changes
  const handleSceneUndo = () => {
    console.log('=== UNDO SCENE CHANGES ===');
    console.log('Original scene data:', originalSceneData);
    
    if (!originalSceneData) {
      console.log('No original data to revert to');
      return;
    }
    
    // Show confirmation dialog
    const confirmed = window.confirm('Are you sure you want to undo all changes and revert to the original scene? This action cannot be undone.');
    if (!confirmed) {
      return;
    }
    
    // Revert the scene in the scenes array
    const revertedScenes = scenes.map(scene => 
      scene.sceneNumber === originalSceneData.sceneNumber ? originalSceneData : scene
    );
    setScenes(revertedScenes);
    
    // Revert the main view if this is the current scene
    if (originalSceneData.sceneNumber === currentScene) {
      console.log('Reverting main view to original data');
      setRefinedImageUrl(originalSceneData.imageUrl);
      setSceneDescription(originalSceneData.description || '');
      setCharacterDetails(originalSceneData.characterDetails);
      setSceneTags(originalSceneData.tags || []);
      setSceneGenre(originalSceneData.genre || '');
      setSceneMood(originalSceneData.mood || '');

      // Force image refresh
      setImageUpdateCounter(prev => prev + 1);
    }
    
    // Reset the edit modal
    setCurrentSceneForEdit(originalSceneData);
    setShowSceneEditModal(false);
    setOriginalSceneData(null); // Clear original data after undo
    
    console.log('Scene changes reverted successfully');
  };

  // Navigate to a specific scene
  const handleSceneNavigation = (sceneNumber) => {
    const targetScene = scenes.find(scene => scene.sceneNumber === sceneNumber);
    if (targetScene) {
      setCurrentScene(sceneNumber);
      setRefinedImageUrl(targetScene.imageUrl);
      setSceneDescription(targetScene.description || '');
      setCharacterDetails(targetScene.characterDetails);
      setSceneTags(targetScene.tags || []);
      setSceneGenre(targetScene.genre || '');
      setSceneMood(targetScene.mood || '');

      // Prevent carryover: clear non-character tags, reset objects, and clear action bindings when switching scenes
      setObjects([]);
      setActionBindings([]); // Clear action bindings - each scene should have its own actions
      setSceneTags(prev => prev.filter(tag => !!tag.characterData));

      setCurrentStep('scene-view');
    }
  };

  // Get scene completion status
  const getSceneCompletionStatus = () => {
    return scenes.length;
  };

  // Handle Book View navigation
  const handleBookView = () => {
    // Include current scene if it exists but hasn't been saved to scenes array yet
    let allScenes = [...scenes];
    
    // Check if current scene exists but isn't in scenes array
    const currentSceneExists = refinedImageUrl && sceneDescription && sceneDescription.trim() !== '';
    const currentSceneInArray = scenes.find(scene => scene.sceneNumber === currentScene);
    
    if (currentSceneExists && !currentSceneInArray) {
      // Add current scene to the book view
      const currentSceneData = {
        imageUrl: refinedImageUrl,
        title: `Scene ${currentScene}`,
        description: sceneDescription
      };
      allScenes.push(currentSceneData);
    }
    
    // Prepare book data in the format expected by BookView
    const bookData = {
      title: storyTitle || 'Untitled Story',
      author: currentUser?.displayName || 'Anonymous',
      coverUrl: allScenes[0]?.imageUrl || refinedImageUrl || '',
      scenes: allScenes,
      about: 'Story created with storyboarding technology',
      brief: 'A story created using storyboarding technology'
    };
    
    console.log('Navigating to BookView with data:', bookData);
    navigate('/view-book', { state: bookData });
  };

  // Handle navigation back to conceptualization
  const handleBackToConceptualization = () => {
    // Close any open modals when navigating back to conceptualization
    setShowUploadArtworkModal(false);
    setShowBFLDetailsModal(false);
    // Preserve all current data and go back to conceptualization step
    setCurrentStep('conceptualization');
    // Reset to step 1 but keep all the data
    setConceptualizationStep(1);
    console.log('Navigating back to conceptualization with preserved data');
  };

  // Navigation functions for conceptualization sub-steps
  const handleNextConceptualizationStep = () => {
    if (conceptualizationStep === 1) {
      // Step 1 (Character Details & Tagging) → Step 2 when sketch+brief exist and at least one tag
      if (sketchFile && sceneBrief.trim() && sceneTags.length > 0) {
        setConceptualizationStep(2);
      }
    } else if (conceptualizationStep === 2) {
      // Step 2 (Scene Description & Genre) → Step 3 when filled
      if (sceneDescription && sceneDescription.trim() && sceneGenre) {
        setShowTagPlacementConfirmation(true);
      }
    }
  };

  const handlePreviousConceptualizationStep = () => {
    if (conceptualizationStep > 1) {
      // Cancel scene conceptualization if in progress
      if (sceneConceptualizationInProgress && sceneConceptualizationAbortController) {
        sceneConceptualizationAbortController.abort();
        setSceneConceptualizationInProgress(false);
        setSceneConceptualizationAbortController(null);
        console.log('Scene conceptualization cancelled due to back navigation');
      }
      setConceptualizationStep(prev => prev - 1);
    }
  };

  const resetConceptualizationStep = () => {
    setConceptualizationStep(1);
    // Cancel any ongoing scene conceptualization
    if (sceneConceptualizationInProgress && sceneConceptualizationAbortController) {
      sceneConceptualizationAbortController.abort();
      setSceneConceptualizationInProgress(false);
      setSceneConceptualizationAbortController(null);
    }
    // Also clear transient scene items and non-character tags when resetting
    setObjects([]);
    setSceneTags(prev => prev.filter(tag => !!tag.characterData));
  };

  const canProceedToNextStep = () => {
    if (conceptualizationStep === 1) {
      // Require sketch, brief, and at least one tag
      return !!sketchFile && sceneBrief.trim() !== '' && sceneTags.length > 0;
    }
    if (conceptualizationStep === 2) {
      // Require description and genre
      return sceneDescription && sceneDescription.trim() !== '' && !!sceneGenre;
    }
    return true;
  };

  const handleTagPlacementConfirmation = () => {
    console.log('🎯 handleTagPlacementConfirmation called');
    console.log('📊 Current state before confirmation:', {
      conceptualizationStep,
      sceneTags: sceneTags.length,
      sceneDescription,
      
      sceneGenre
    });
    
    setShowTagPlacementConfirmation(false);
    
    // Show conceptualization loader immediately
    setShowSceneConceptualizationLoader(true);
    
    // Start scene conceptualization in background immediately after Step 2
    console.log('🚀 Calling startSceneConceptualization from Step 2...');
    
    // Add timeout wrapper to prevent infinite hanging
    // Upload can take 3-4 minutes for large files, then conceptualization takes 30-40s
    // So we need at least 5-6 minutes total
    const conceptualizationPromise = startSceneConceptualization(false);
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error('Scene conceptualization is taking too long. Please try again.'));
      }, 420000); // 7 minutes timeout (420s) to account for slow uploads + processing
    });
    
    // Safety timeout to ensure loader is always cleared (8 minutes max)
    const safetyTimeout = setTimeout(() => {
      console.error('⚠️ SAFETY TIMEOUT: Force clearing loader after 8 minutes');
      setShowSceneConceptualizationLoader(false);
      setSceneConceptualizationInProgress(false);
      setSceneImageLoading(false);
      setSceneConceptualizationAbortController(null);
    }, 480000); // 8 minutes safety net
    
    Promise.race([conceptualizationPromise, timeoutPromise])
      .then(() => {
        clearTimeout(safetyTimeout);
        // Conceptualization complete - go directly to scene view (skip Step 3)
        console.log('✅ Conceptualization complete, moving directly to scene view');
        setShowSceneConceptualizationLoader(false);
        setSceneConceptualizationInProgress(false);
        setCurrentStep('scene-view'); // Go directly to scene view, skip Step 3
      })
      .catch((error) => {
        clearTimeout(safetyTimeout);
        console.error('❌ Conceptualization failed:', error);
        // Always clear loader and state, even if error handling fails
        setShowSceneConceptualizationLoader(false);
        setSceneConceptualizationInProgress(false);
        setSceneImageLoading(false);
        setSceneConceptualizationAbortController(null);
        // Content moderation: modal already shown, do not alert; user stays on conceptualization to edit and retry
        if (error.message === 'CONTENT_MODERATION') return;
        // Provide user-friendly error message for other failures
        const errorMessage = error.message || 'Scene conceptualization failed. Please try again.';
        console.error('Error details:', { message: errorMessage, name: error.name, stack: error.stack });
        alert(errorMessage);
      });
  };

  const handleTagPlacementCancel = () => {
    setShowTagPlacementConfirmation(false);
  };

  // Insert object from library into current scene and auto-place tag
  const handleInsertObjectFromLibrary = (libItem) => {
    if (!libItem?.name) return;
    const placedX = Math.min(100, Math.max(0, libItem.x ?? 50));
    const placedY = Math.min(100, Math.max(0, libItem.y ?? 50));
    const newObject = {
      id: uuidv4(),
      name: libItem.name,
      description: libItem.description || '',
      x: placedX,
      y: placedY
    };
    setObjects(prev => [...prev, newObject]);

    const newTag = {
      id: uuidv4(),
      name: libItem.name,
      x: placedX,
      y: placedY,
      objectData: {
        name: libItem.name,
        type: 'object',
        description: libItem.description || ''
      }
    };
    setSceneTags(prev => [...prev, newTag]);
    setShowSceneObjectLibrary(false);
  };

  const preloadGeneratedImage = (url) => {
    return new Promise((resolve, reject) => {
      if (!url) return resolve();
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = (err) => reject(err);
      img.src = url;
    });
  };

  const startSceneConceptualization = async (showLoader = false) => {
    console.log('🚀 startSceneConceptualization called with showLoader:', showLoader);
    console.log('🔍 Current state:', {
      sceneConceptualizationInProgress,
      sketchUrl: sketchUrl ? 'present' : 'missing',
      sceneDescription: sceneDescription ? 'present' : 'missing',
      sceneTags: sceneTags.length,
      backendConfig: backendConfig
    });

    if (sceneConceptualizationInProgress) {
      console.log('⚠️ Scene conceptualization already in progress');
      return;
    }

    const abortController = new AbortController();
    setSceneConceptualizationAbortController(abortController);
    setSceneConceptualizationInProgress(true);
    setSceneImageLoading(true);
    
    // Only show loader if explicitly requested (Step 3)
    if (showLoader) {
      setShowSceneConceptualizationLoader(true);
    }

    try {
      console.log('🎯 Starting enhanced scene conceptualization...');
      
      // Step 1: Compress and optimize sketch before sending
      // If it's a large data URL (>1MB), compress it to reduce payload size
      let finalSketchUrl = sketchUrl;
      if (sketchUrl && sketchUrl.startsWith('data:')) {
        const dataUrlSize = sketchUrl.length * 3 / 4; // Approximate size in bytes
        const sizeMB = dataUrlSize / 1024 / 1024;
        console.log('📤 Sketch is a data URL, size:', sizeMB.toFixed(2), 'MB');
        
        // If data URL is larger than 1MB, compress it
        if (dataUrlSize > 1024 * 1024) {
          console.log('📦 Compressing large data URL to reduce payload size...');
          try {
            const compressed = await new Promise((resolve) => {
              const img = new Image();
              img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;
                const maxWidth = 1920;
                
                if (width > maxWidth) {
                  height = (height * maxWidth) / width;
                  width = maxWidth;
                }
                
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);
                
                const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.85);
                const compressedSize = (compressedDataUrl.length * 3 / 4 / 1024 / 1024).toFixed(2);
                console.log('✅ Compressed to:', compressedSize, 'MB');
                resolve(compressedDataUrl);
              };
              img.onerror = () => resolve(sketchUrl); // Fallback to original
              img.src = sketchUrl;
            });
            finalSketchUrl = compressed;
          } catch (error) {
            console.warn('⚠️ Compression failed, using original:', error);
            finalSketchUrl = sketchUrl;
          }
        } else {
          console.log('✅ Data URL size is acceptable, using as-is');
        }
      } else {
        console.log('📋 Using existing sketch URL (not a data URL):', sketchUrl);
      }
      
      // Prepare data for enhanced backend endpoint
      // First, deduplicate characters by name and imageUrl to avoid sending duplicates
      const uniqueCharacters = sceneTags
        .filter(tag => tag.characterData) // Only character tags
        .reduce((acc, tag) => {
          const key = `${tag.characterData.name}_${tag.characterData.imageUrl}`;
          if (!acc.has(key)) {
            acc.set(key, tag);
          }
          return acc;
        }, new Map());
      
      const characterTags = Array.from(uniqueCharacters.values())
        .map(tag => ({
          id: tag.id,
          name: tag.characterData.name,
          age: tag.characterData.age || '', // Add age field
          position: { x: tag.x, y: tag.y },
          gender: tag.characterData.gender || '',
          clothing: tag.characterData.appearance || tag.characterData.clothes || '',
          description: tag.characterData.description || '',
          actions: tag.characterData.actions || '',
          imageUrl: tag.characterData.imageUrl || '', // Include the character's specific image URL
          type: 'character'
        }));

      const sceneTagsForBackend = sceneTags
        .filter(tag => !tag.characterData) // Only non-character tags (objects, surroundings)
        .map(tag => ({
          id: tag.id,
          name: tag.name,
          position: { x: tag.x, y: tag.y },
          type: tag.objectData ? 'object' : tag.surroundingData ? 'environment' : 'other',
          description: tag.objectData?.description || tag.surroundingData?.description || '',
          objectName: tag.objectData?.name || tag.name || '',
          reason: tag.objectData?.reason || tag.surroundingData?.reason || ''
        }));

      // Auto-bind actions from text and proximity
      const autoBindings = [];
      const narrativeText = `${sceneDescription || ''} ${sceneBrief || ''}`.toLowerCase();
      const specs = [
        {
          key: 'guitar',
          keywords: ['guitar', 'guitars', 'guitarist', 'guitar player'],
          verbs: ['play', 'playing', 'strum', 'strumming'],
          relation: 'holding',
          grip: 'both hands'
        },
        {
          key: 'drum',
          keywords: ['drum', 'drums', 'drummer', 'drumset', 'drum set'],
          verbs: ['play', 'playing', 'drum', 'drumming', 'beat', 'beating'],
          relation: 'in front of',
          grip: 'both hands'
        },
        {
          key: 'mic',
          keywords: ['mic', 'microphone'],
          verbs: ['sing', 'singing', 'speak', 'speaking', 'talk', 'talking', 'perform', 'performing'],
          relation: 'holding',
          grip: 'one hand'
        }
      ];

      const findObjects = (keywords) => {
        const kws = Array.isArray(keywords) ? keywords : [keywords];
        return sceneTagsForBackend.filter(t => {
          const nm = (t.objectName || t.name || '').toLowerCase();
          return kws.some(k => nm.includes(k));
        });
      };

      const nearestObject = (char, keywords) => {
        let best = null; let bestDist = Infinity;
        const pool = findObjects(keywords);
        pool.forEach(t => {
          const dx = (t.position?.x || 0) - (char.position?.x || 0);
          const dy = (t.position?.y || 0) - (char.position?.y || 0);
          const d = dx*dx + dy*dy;
          if (d < bestDist) { bestDist = d; best = t; }
        });
        return best;
      };

      // Keep original binding behaviour intact (no extra aggressive fallback to avoid changing backend expectations)
      characterTags.forEach(char => {
        const cname = (char.name || '').toLowerCase();
        specs.forEach(spec => {
          const mentioned = spec.verbs.some(v => narrativeText.includes(`${cname} ${v}`) || narrativeText.includes(`${v} ${cname}`))
            && spec.keywords.some(k => narrativeText.includes(k));
          if (mentioned) {
            let obj = nearestObject(char, spec.keywords);
            if (obj) {
              autoBindings.push({
                actorTagId: char.id,
                objectTagId: obj.id,
                action: `playing ${spec.key}`,
                relation: spec.relation,
                grip: spec.grip,
                actorName: char.name,
                objectName: spec.key
              });
            }
          }
        });
      });

      // Get the first character's image URL for selectedCharacterImageUrl
      const firstCharacter = sceneTags.find(tag => tag.characterData);
      const selectedCharacterImageUrl = firstCharacter?.characterData?.imageUrl || '';

      const finalActionBindings = actionBindings && actionBindings.length ? actionBindings : autoBindings;
      
      console.log('🔗 ACTION BINDINGS DEBUG:');
      console.log('   actionBindings state:', actionBindings?.length || 0, 'bindings');
      console.log('   autoBindings generated:', autoBindings.length, 'bindings');
      console.log('   finalActionBindings being sent:', finalActionBindings.length, 'bindings');
      if (finalActionBindings.length > 0) {
        finalActionBindings.forEach((b, idx) => {
          console.log(`   Binding ${idx + 1}: ${b.actorName || 'unknown'} → ${b.action || 'unknown'} ${b.objectName || 'unknown'}`);
        });
      } else {
        console.log('   ⚠️ NO ACTION BINDINGS - will not enforce character-object interactions');
      }

      // Content moderation check before sending to API
      const textToCheck = [
        sceneBrief || '',
        sceneDescription || '',
        ...characterTags.map(char => [
          char.name || '',
          char.description || '',
          char.clothing || '',
          char.characterData?.name || '',
          char.characterData?.description || '',
          char.characterData?.appearance || ''
        ].join(' ')),
        ...sceneTagsForBackend.map(tag => [
          tag.name || '',
          tag.description || '',
          tag.objectName || ''
        ].join(' '))
      ].join(' ');
      
      const moderationCheck = checkContentModeration(textToCheck);
      
      if (moderationCheck.isInappropriate) {
        console.warn('Content moderation triggered for scene generation', {
          matchedKeywords: moderationCheck.matchedKeywords
        });
        const errorMessage = getContentModerationMessage('scene', moderationCheck.matchedKeywords);
        setContentModerationMessage(errorMessage);
        setContentModerationKeywords(moderationCheck.matchedKeywords);
        setShowContentModerationModal(true);
        setSceneConceptualizationInProgress(false);
        setSceneImageLoading(false);
        if (showLoader) {
          setShowSceneConceptualizationLoader(false);
        }
        // Reject so caller does not advance to scene-view; user stays on conceptualization to edit and retry
        throw new Error('CONTENT_MODERATION');
      }

      const requestData = {
        sketchUrl: finalSketchUrl,
        selectedCharacterImageUrl: selectedCharacterImageUrl,
        characterTags: characterTags,
        sceneTags: sceneTagsForBackend,
        actionBindings: finalActionBindings,
        sceneBrief: sceneBrief,
        userId: auth.currentUser?.uid,
        storyId: storyId
      };

      console.log('📋 Enhanced scene conceptualization request:', requestData);
      // URL construction for Firebase Functions
      // Firebase Functions baseUrl is https://...cloudfunctions.net/api
      // Express app routes are prefixed with /api, so route is /api/story-generation/enhanced-conceptualize-scene
      // Combined: /api/api/story-generation/enhanced-conceptualize-scene
      let apiPath;
      if (backendConfig.baseUrl.includes('cloudfunctions.net/api')) {
        // Production Firebase Functions - baseUrl ends with /api, route needs /api/story-generation
        // This creates /api/api/story-generation/enhanced-conceptualize-scene
        apiPath = '/api/story-generation/enhanced-conceptualize-scene';
      } else {
        // Local development - baseUrl doesn't have /api, route is /api/story-generation
        apiPath = '/api/story-generation/enhanced-conceptualize-scene';
      }
      const backendUrl = `${backendConfig.baseUrl}${apiPath}`;
      console.log('🌐 Backend URL:', backendUrl);
      
      // Debug character data
      console.log('🔍 Character Tags Debug:');
      console.log('🔍 Original sceneTags count:', sceneTags.length);
      console.log('🔍 Unique characters after deduplication:', characterTags.length);
      characterTags.forEach((char, index) => {
        console.log(`   Character ${index + 1}: ${char.name} - Image: ${char.imageUrl}`);
        console.log(`   Character ${index + 1} Full Details:`, char);
      });
      console.log('🔍 Selected Character Image URL:', selectedCharacterImageUrl);
      console.log('🔍 All sceneTags (raw):', sceneTags);
      console.log('🔍 Character tags count:', characterTags.length);
      console.log('🔍 Scene tags count:', sceneTagsForBackend.length);

      // Add timeout to prevent hanging (5 minutes for complex scenes with multiple characters and action bindings)
      // With image compression, payloads are now ~200-500KB instead of 4-5MB
      // But character merging and action binding processing can take additional time
      // Increased timeout to 5 minutes to allow backend to complete even for complex scenes
      const timeoutId = setTimeout(() => {
        console.warn('⚠️ Fetch timeout triggered - aborting request');
        abortController.abort();
      }, 300000); // 5 minutes timeout (300s) - allows time for complex scene generation

      console.log('🌐 Making API request to:', backendUrl);
      console.log('📦 Request payload size:', JSON.stringify(requestData).length, 'bytes');
      
      // Add a shorter timeout for the initial request (90 seconds)
      // Firebase Functions should respond quickly even if job is queued, but allow more time for large payloads
      const requestTimeoutId = setTimeout(() => {
        console.warn('⚠️ Initial request taking longer than 90s - aborting and showing error');
        abortController.abort();
      }, 90000); // 90 seconds for initial response (allows time for large payload upload)
      
      let response;
      try {
        console.log('📤 Sending fetch request...');
        const fetchStartTime = Date.now();
        
        response = await fetch(backendUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData),
          signal: abortController.signal,
          // Add keepalive to prevent connection from being dropped
          keepalive: false
        });
        
        const fetchDuration = Date.now() - fetchStartTime;
        console.log(`✅ Fetch completed in ${fetchDuration}ms`);
        clearTimeout(requestTimeoutId);
        console.log('📡 Response received, status:', response.status);
        clearTimeout(timeoutId);
      } catch (fetchError) {
        clearTimeout(requestTimeoutId);
        clearTimeout(timeoutId);
        
        console.error('❌ Fetch error details:', {
          name: fetchError.name,
          message: fetchError.message,
          stack: fetchError.stack
        });
        
        if (fetchError.name === 'AbortError') {
          throw new Error('Scene conceptualization timed out after 90 seconds. The request may be too large or the server is slow. Please try again.');
        }
        
        // Handle network errors (backend down, connection refused, etc.)
        if (fetchError.message?.includes('Failed to fetch') || 
            fetchError.message?.includes('NetworkError') ||
            fetchError.message?.includes('ECONNREFUSED') ||
            fetchError.message?.includes('Network request failed') ||
            fetchError.message?.includes('Load failed') ||
            fetchError.message?.includes('network connection was lost') ||
            fetchError.code === 'ECONNREFUSED' ||
            !navigator.onLine) {
          throw new Error('Cannot connect to backend server. The connection was lost. Please check your internet connection and try again.');
        }
        throw fetchError;
      }

      console.log('📡 Response status:', response.status);
      console.log('📡 Response ok:', response.ok);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (errorData.error === 'Content moderation violation') {
          const errorMessage = errorData.message || getContentModerationMessage('scene', errorData.matchedKeywords || []);
          setContentModerationMessage(errorMessage);
          setContentModerationKeywords(errorData.matchedKeywords || []);
          setShowContentModerationModal(true);
          setSceneConceptualizationInProgress(false);
          setSceneImageLoading(false);
          if (showLoader) {
            setShowSceneConceptualizationLoader(false);
          }
          // Reject so caller does not advance to scene-view; user stays on conceptualization to edit and retry
          throw new Error('CONTENT_MODERATION');
        }
        const errorText = await response.text().catch(() => errorData.details || 'Unknown error');
        console.error('❌ Response error text:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ Enhanced scene conceptualization result:', result);
      
      // Handle job-based async response (production mode)
      if (result.success && result.jobId && (result.status === 'pending' || result.status === 'queued')) {
        console.log('🔄 Job queued, polling for status...', { jobId: result.jobId });
        
        // Poll for job status
        const pollJobStatus = async (jobId, maxAttempts = 60, interval = 3000) => {
          // Fix URL construction for Firebase Functions
          // Job status route is at /api/job-status/:jobId
          let statusPath;
          if (backendConfig.baseUrl.includes('cloudfunctions.net/api')) {
            // Production Firebase Functions - baseUrl already has /api, route is /api/job-status
            statusPath = `/job-status/${jobId}`;
          } else {
            // Local development
            statusPath = `/api/job-status/${jobId}`;
          }
          const statusUrl = `${backendConfig.baseUrl}${statusPath}`;
          console.log('🔄 Polling job status at:', statusUrl);
          
          for (let attempt = 0; attempt < maxAttempts; attempt++) {
            // Wait before first attempt (except for attempt 0)
            if (attempt > 0) {
              await new Promise(resolve => setTimeout(resolve, interval));
            }
            
            console.log(`🔄 Polling attempt ${attempt + 1}/${maxAttempts}...`);
            
            try {
              // Build headers with authentication
              const headers = {
                'Content-Type': 'application/json'
              };
              
              // Add user ID for authentication (queue endpoint requires userId)
              if (auth.currentUser?.uid) {
                headers['x-user-id'] = auth.currentUser.uid;
                // Also try to get auth token if available
                try {
                  const token = await auth.currentUser.getIdToken();
                  if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                  }
                } catch (tokenError) {
                  console.warn('Could not get auth token for polling:', tokenError);
                }
              }
              
              const statusResponse = await fetch(statusUrl, {
                method: 'GET',
                headers: headers
              });
              
              if (!statusResponse.ok) {
                const errorText = await statusResponse.text();
                console.warn(`⚠️ Job status check failed (attempt ${attempt + 1}):`, statusResponse.status, errorText);
                if (statusResponse.status === 404) {
                  throw new Error('Job not found');
                }
                if (statusResponse.status === 401 || statusResponse.status === 403) {
                  throw new Error('Authentication failed for job status check');
                }
                continue;
              }
              
              const statusData = await statusResponse.json();
              console.log(`📊 Job status (attempt ${attempt + 1}):`, statusData);
              
              // Check state field (Bull queue uses 'state', not 'status')
              const jobState = statusData.state || statusData.status;
              
              if (jobState === 'completed' && statusData.result) {
                console.log('✅ Job completed!', statusData.result);
                // Use the job result as if it was the direct response
                // The result should have the same structure as the direct API response
                return statusData.result;
              } else if (jobState === 'failed' || jobState === 'error') {
                throw new Error(statusData.error || statusData.message || 'Job failed');
              } else if (jobState === 'active' || jobState === 'processing' || jobState === 'pending' || jobState === 'queued' || jobState === 'waiting') {
                // Continue polling
                continue;
              } else {
                console.warn(`⚠️ Unknown job state: ${jobState}`, statusData);
                continue;
              }
            } catch (pollError) {
              console.error('Polling error:', pollError);
              console.warn(`⚠️ Polling error (attempt ${attempt + 1}):`, pollError);
              if (attempt === maxAttempts - 1) {
                throw new Error(`Job polling timed out: ${pollError.message || 'Please try again.'}`);
              }
            }
          }
          
          throw new Error('Job did not complete in time. Please try again.');
        };
        
        // Poll and get the result
        const jobResult = await pollJobStatus(result.jobId);
        
        // Merge job result into result object for consistent handling
        Object.assign(result, jobResult);
      }
      
      if (result.success) {
        console.log('✅ Enhanced scene conceptualization completed!');
        console.log('📊 Response:', result);
        
        // Log the BFL prompt if available
        if (result.bflPrompt) {
          console.log('🎨 BFL PROMPT SENT TO BFL:');
          console.log('=================================================================');
          console.log(result.bflPrompt);
          console.log('=================================================================');
          console.log('📊 BFL Prompt Analysis:');
          console.log('   Length:', result.bflPrompt.length, 'characters');
          console.log('   Estimated Tokens:', Math.ceil(result.bflPrompt.length / 4));
          console.log('   Target: 490-500 tokens');
        }
        
        // Store BFL details for display
        if (result.bflPrompt || result.bflInputImage || result.bflControlImage) {
          setBflDetails({
            prompt: result.bflPrompt,
            inputImage: result.bflInputImage,
            controlImage: result.bflControlImage,
            inputImageUrl: result.bflInputImageUrl,
            mergedCharacterImageUrl: result.bflMergedCharacterImageUrl || result.bflInputImageUrl, // URL of merged character image
            aspectRatio: result.aspectRatio
          });
        }
        
        // Clean up the scene image URL - backend might return malformed URL
        // Check multiple possible field names for the image URL
        let fullSceneImageUrl = result.sceneImageUrl || result.generatedImageUrl || result.bflImageUrl || result.result?.sceneImageUrl || result.result?.generatedImageUrl;
        
        console.log('Original result.sceneImageUrl:', result.sceneImageUrl);
        
        // Fix malformed URL where API endpoint is incorrectly prepended
        if (fullSceneImageUrl && (fullSceneImageUrl.includes('cloudfunctions.net/apihttps://') || fullSceneImageUrl.includes('apihttps://'))) {
          // Extract the Firebase Storage URL part after the API endpoint
          // Pattern: ...apihttps://storage.googleapis.com/...
          const storageUrlMatch = fullSceneImageUrl.match(/api(https:\/\/[^\s\?]+)/);
          if (storageUrlMatch && storageUrlMatch[1]) {
            fullSceneImageUrl = storageUrlMatch[1];
            console.log('Cleaned malformed URL, extracted:', fullSceneImageUrl);
          } else {
            // Try alternative: split at 'apihttps://'
            const splitIndex = fullSceneImageUrl.indexOf('apihttps://');
            if (splitIndex !== -1) {
              fullSceneImageUrl = fullSceneImageUrl.substring(splitIndex + 3); // +3 to skip 'api'
              console.log('Cleaned malformed URL using split, extracted:', fullSceneImageUrl);
            }
          }
        }
        
        // If URL is already a full Firebase Storage URL, use it directly
        if (fullSceneImageUrl && (fullSceneImageUrl.startsWith('http://') || fullSceneImageUrl.startsWith('https://'))) {
          // Already a full URL - add cache-busting if not present
          if (fullSceneImageUrl.includes('?t=')) {
            fullSceneImageUrl = fullSceneImageUrl.replace(/\?t=\d+/, `?t=${Date.now()}`);
          } else {
            fullSceneImageUrl = `${fullSceneImageUrl}?t=${Date.now()}`;
          }
        } else if (fullSceneImageUrl && fullSceneImageUrl.startsWith('/')) {
          // Relative path - this should NOT happen in production (backend should return Firebase Storage URL)
          // But if it does, use backend proxy endpoint instead of localhost
          const isProduction = window.location.hostname !== 'localhost' && !window.location.hostname.includes('127.0.0.1');
          if (isProduction && fullSceneImageUrl.startsWith('/uploads/')) {
            // In production, use backend proxy to fetch from Firebase Storage
            const backendBase = backendConfig.baseUrl || 'https://us-central1-artmosphere-89b64.cloudfunctions.net/api';
            fullSceneImageUrl = `${backendBase}/proxy-image?url=${encodeURIComponent(fullSceneImageUrl)}?t=${Date.now()}`;
            console.warn('⚠️ Backend returned relative path in production, using proxy:', fullSceneImageUrl);
          } else {
            // Local development or non-uploads path - prepend backend URL
            fullSceneImageUrl = `${backendConfig.baseUrl}${fullSceneImageUrl}?t=${Date.now()}`;
          }
        }
        
        console.log('Setting refinedImageUrl to:', fullSceneImageUrl);
        console.log('Previous refinedImageUrl was:', refinedImageUrl);
        
        // Set the image URL immediately (don't wait for preload)
        setRefinedImageUrl(fullSceneImageUrl);
        setSceneImageLoading(false);
        
        // Preload in background (non-blocking) and verify it loads
        console.log('🖼️ Starting background image preload...');
        preloadGeneratedImage(fullSceneImageUrl)
          .then(() => {
            console.log('✅ Image preloaded successfully');
          })
          .catch((preloadError) => {
            console.error('❌ Image preload failed:', {
              url: fullSceneImageUrl,
              error: preloadError,
              message: preloadError.message
            });
            // Don't clear the URL - let the onError handler show the error
            // But log it for debugging
          });
        preloadGeneratedImage(fullSceneImageUrl)
          .then(() => {
            console.log('✅ Image preloaded successfully');
          })
          .catch((preloadErr) => {
            console.warn('⚠️ Scene image preload failed (non-critical):', preloadErr);
          });
        
        // Automatically create a scene when image is generated
        if (fullSceneImageUrl && currentScene) {
          console.log('🎬 Automatically creating scene for current scene:', currentScene);
          const newScene = {
            id: `scene_${currentScene}_${Date.now()}`,
            sceneNumber: currentScene,
            sceneTitle: `Scene ${currentScene}`,
            sceneDescription: sceneDescription || `Scene ${currentScene} description`,
            refinedImageUrl: fullSceneImageUrl,
            imageUrl: fullSceneImageUrl,
            characterDetails: characterDetails,
            tags: sceneTags,
            genre: sceneGenre,
            mood: sceneMood,
            createdAt: new Date().toISOString()
          };
          
          setScenes(prev => {
            // Replace existing scene or add new one
            const existingIndex = prev.findIndex(scene => scene.sceneNumber === currentScene);
            let updatedScenes;
            if (existingIndex >= 0) {
              updatedScenes = [...prev];
              updatedScenes[existingIndex] = newScene;
              console.log('🔄 Updated existing scene:', newScene);
            } else {
              updatedScenes = [...prev, newScene];
              console.log('➕ Added new scene:', newScene);
            }
            
            return updatedScenes;
          });
          
          // Save to Firebase AFTER setScenes completes (non-blocking)
          if (storyId) {
            console.log('💾 Auto-saving story with new scene (non-blocking)...');
            // Use setTimeout to ensure setScenes completes first
            setTimeout(() => {
              const storyData = {
                userId: currentUser.uid, // Use the verified UID
                storyTitle,
                storySetupData,
                scenes: [], // Will be updated from state
                totalScenes: 0,
                coverUrl: fullSceneImageUrl,
                createdAt: new Date(),
                updatedAt: new Date(),
                status: 'in-progress'
              };
              
              // Get current scenes from state (use a ref or get it differently)
              const storyRef = doc(db, 'stories', storyId);
              // Use getDoc to get current scenes, then update
              getDoc(storyRef).then(docSnap => {
                const currentScenes = docSnap.exists() ? docSnap.data().scenes || [] : [];
                const existingIndex = currentScenes.findIndex(s => s.sceneNumber === currentScene);
                let updatedScenes;
                if (existingIndex >= 0) {
                  updatedScenes = [...currentScenes];
                  updatedScenes[existingIndex] = {
                    id: `scene_${currentScene}_${Date.now()}`,
                    sceneNumber: currentScene,
                    sceneTitle: `Scene ${currentScene}`,
                    sceneDescription: sceneDescription || `Scene ${currentScene} description`,
                    refinedImageUrl: fullSceneImageUrl,
                    imageUrl: fullSceneImageUrl,
                    characterDetails: characterDetails,
                    tags: sceneTags,
                    genre: sceneGenre,
                    mood: sceneMood,
                    createdAt: new Date().toISOString()
                  };
                } else {
                  updatedScenes = [...currentScenes, {
                    id: `scene_${currentScene}_${Date.now()}`,
                    sceneNumber: currentScene,
                    sceneTitle: `Scene ${currentScene}`,
                    sceneDescription: sceneDescription || `Scene ${currentScene} description`,
                    refinedImageUrl: fullSceneImageUrl,
                    imageUrl: fullSceneImageUrl,
                    characterDetails: characterDetails,
                    tags: sceneTags,
                    genre: sceneGenre,
                    mood: sceneMood,
                    createdAt: new Date().toISOString()
                  }];
                }
                
                return updateDoc(storyRef, {
                  ...storyData,
                  scenes: updatedScenes,
                  totalScenes: updatedScenes.length,
                  coverUrl: updatedScenes.length > 0 ? (updatedScenes[0]?.refinedImageUrl || updatedScenes[0]?.imageUrl || null) : null
                });
              })
              .then(() => {
                console.log('✅ Story auto-saved successfully');
              })
              .catch((error) => {
                console.error('❌ Auto-save failed:', error);
              });
            }, 100); // Small delay to let setScenes complete
          }
        }
        
        // Skip Step 3 - navigation to scene-view is handled by the caller
        // The caller (handleTagPlacementConfirmation) will move directly to scene-view after conceptualization completes
        console.log('✅ startSceneConceptualization completed successfully');
      } else {
        throw new Error(result.error || 'Scene conceptualization failed');
      }

    } catch (error) {
      console.error('❌ Enhanced scene conceptualization error:', error);
      if (error.name === 'AbortError') {
        console.log('Scene conceptualization was cancelled or timed out');
      }
      
      // Always hide loader and reset state on error - regardless of who set it
      setShowSceneConceptualizationLoader(false);
      setSceneConceptualizationInProgress(false);
      setSceneImageLoading(false);
      setSceneConceptualizationAbortController(null);
      
      // Show error message if loader was visible (content moderation already shows modal, do not alert)
      if (error.message === 'CONTENT_MODERATION') {
        error.handled = true;
      } else if (showLoader || showSceneConceptualizationLoader) {
        const errorMessage = error.message || 'Scene conceptualization failed. Please try again.';
        console.error('Showing error to user:', errorMessage);
        if (showLoader) {
          alert(errorMessage);
        }
        error.handled = true;
      }
      // Re-throw the error so the caller can handle it
      throw error;
    } finally {
      // Ensure state is always reset
      setSceneConceptualizationInProgress(false);
      setSceneConceptualizationAbortController(null);
      // Only hide loader if it was set by this function (showLoader = true)
      // If caller set it, they should handle clearing it
      if (showLoader) {
        setShowSceneConceptualizationLoader(false);
      }
      setSceneImageLoading(false);
    }
  };

  // New function for Step 3 that shows loader and waits for completion
  const handleStep3GenerateScene = async () => {
    const quotaCheck = checkQuotaBeforeUse('storyboarding');
    if (!quotaCheck.allowed) return;
    await recordUsage('storyboarding');

    if (sceneConceptualizationInProgress) {
      // If already in progress, just show the loader and wait
      setShowSceneConceptualizationLoader(true);
      
      // Wait for completion
      const checkCompletion = setInterval(() => {
        if (!sceneConceptualizationInProgress && refinedImageUrl) {
          clearInterval(checkCompletion);
          setShowSceneConceptualizationLoader(false);
          setCurrentStep('scene-view');
        }
      }, 500);
      
      // Timeout after 2 minutes
      setTimeout(() => {
        clearInterval(checkCompletion);
        if (sceneConceptualizationInProgress) {
          setShowSceneConceptualizationLoader(false);
          alert('Scene generation is taking longer than expected. Please try again.');
        }
      }, 120000);
      
    } else {
      // If not in progress, start it with loader
      try {
        await startSceneConceptualization(true);
        setShowSceneConceptualizationLoader(false);
        setCurrentStep('scene-view');
      } catch (err) {
        if (err.message === 'CONTENT_MODERATION') {
          // Modal already shown; user stays on conceptualization to edit and retry
          return;
        }
        setShowSceneConceptualizationLoader(false);
        setSceneConceptualizationInProgress(false);
        alert(err.message || 'Scene conceptualization failed. Please try again.');
      }
    }
  };

  // Load user's character library
  const loadUserCharacterLibrary = async () => {
    if (!auth.currentUser) return;
    
    try {
      console.log('Loading character library for user:', auth.currentUser.uid);
      const response = await fetch(`${API_ENDPOINTS.getUserLibrary}/${auth.currentUser.uid}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Character library response:', data);
        console.log('Raw characters data:', data.characters);
        
        // Debug each character's ID
        if (data.characters && data.characters.length > 0) {
          data.characters.forEach((char, index) => {
            console.log(`Character ${index}:`, {
              id: char.id,
              characterId: char.characterId,
            name: char.name,
              hasId: !!char.id,
              hasCharacterId: !!char.characterId
          });
        });
        }
        
        setUserCharacters(data.characters || []);
        console.log('Loaded user characters:', data.characters);
      } else {
        console.error('Failed to load character library:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error loading character library:', error);
    }
  };

  // Get character image URL from library by name
  const getCharacterImageUrlFromLibrary = (characterName) => {
    const libraryCharacter = userCharacters.find(char => char.name === characterName);
    console.log('=== GET CHARACTER IMAGE URL FROM LIBRARY ===');
    console.log('Looking for character:', characterName);
    console.log('Available library characters:', userCharacters.map(c => ({ name: c.name, hasImageUrl: !!c.imageUrl })));
    console.log('Found library character:', libraryCharacter);
    return libraryCharacter?.imageUrl || null;
  };

  // Ensure character has an imageUrl by checking library or using placeholder
  const ensureCharacterHasImageUrl = (character) => {
    console.log('=== ENSURING CHARACTER HAS IMAGE URL ===');
    console.log('Character to check:', character);
    
    // If character already has imageUrl, return as is
    if (character.imageUrl) {
      console.log('Character already has imageUrl:', character.imageUrl);
      return character;
    }
    
    // Try to get imageUrl from library
    const libraryImageUrl = getCharacterImageUrlFromLibrary(character.name);
    if (libraryImageUrl) {
      console.log('Found imageUrl in library:', libraryImageUrl);
      return { ...character, imageUrl: libraryImageUrl };
    }
    
    // Use placeholder image if no imageUrl found
    console.log('No imageUrl found, using placeholder');
    return { 
      ...character, 
      imageUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop' 
    };
  };

  // Helper: compute the image URL that will be sent to BFL (first tagged character or library fallback)
  const getSelectedCharacterImageUrlForDebug = () => {
    const firstCharacterTag = sceneTags.find(tag => tag.characterData);
    let url = firstCharacterTag?.characterData?.imageUrl || '';
    if (!url && firstCharacterTag?.characterData?.name) {
      url = getCharacterImageUrlFromLibrary(firstCharacterTag.characterData.name) || '';
    }
    return url;
  };

  // Handle character selection from library
  const handleCharacterSelectionFromLibrary = (character) => {
    console.log('=== CHARACTER LIBRARY SELECTION ===');
    console.log('Selected character:', character);
    console.log('Current sceneTags:', sceneTags);
    console.log('Current sketchFile:', sketchFile);
    console.log('Current sceneDescription:', sceneDescription);
    console.log('Button should be enabled:', !!(sketchFile && sceneDescription));

    // Show confirmation modal first
    setSelectedCharacterFromLibrary(character);
    setShowCharacterLibrary(false);
    setCharacterConfirmed(true);
  };

  const handleCharacterConfirmation = () => {
    console.log('=== CHARACTER CONFIRMATION ===');
    console.log('selectedCharacterFromLibrary:', selectedCharacterFromLibrary);
    console.log('Current sketchFile before confirmation:', sketchFile);
    console.log('Current sceneDescription before confirmation:', sceneDescription);
    
    if (selectedCharacterFromLibrary) {
      // Debug the character's imageUrl
      console.log('Character imageUrl check:', {
        name: selectedCharacterFromLibrary.name,
        imageUrl: selectedCharacterFromLibrary.imageUrl,
        hasImageUrl: !!selectedCharacterFromLibrary.imageUrl,
        allFields: selectedCharacterFromLibrary
      });
      
      // Add character to storySetupData
      if (storySetupData) {
        const updatedStorySetupData = {
          ...storySetupData,
          characters: [...storySetupData.characters, selectedCharacterFromLibrary]
        };
        setStorySetupData(updatedStorySetupData);
        console.log('Added character to storySetupData:', selectedCharacterFromLibrary.name);
      }

      // Create tag for the character
      const characterWithImage = ensureCharacterHasImageUrl(selectedCharacterFromLibrary);
      
      const newTag = {
        id: uuidv4(),
        name: characterWithImage.name,
        x: 20 + (sceneTags.length * 20), // Spread characters across the artwork
        y: 20 + (sceneTags.length % 2) * 30,
        characterIndex: `library-${Date.now()}`,
        characterData: {
          ...characterWithImage,
          role: characterWithImage.role || 'Character'
        },
        role: characterWithImage.role || 'Character'
      };
      
      console.log('Created new tag with characterData:', {
        tagName: newTag.name,
        characterDataImageUrl: newTag.characterData.imageUrl,
        hasCharacterDataImageUrl: !!newTag.characterData.imageUrl,
        fullCharacterData: newTag.characterData
      });
      
      setSceneTags(prev => {
        const updated = [...prev, newTag];
        console.log('Updated sceneTags:', updated);
        console.log('sceneTags length after update:', updated.length);
        console.log('First tag characterData:', updated[0]?.characterData);
        console.log('First tag imageUrl:', updated[0]?.characterData?.imageUrl);
        return updated;
      });
      
      console.log('Added library character to sceneTags:', newTag);
      
      // Close confirmation modal
      setCharacterConfirmed(false);
      setSelectedCharacterFromLibrary(null);
      console.log('Character confirmation completed');
      console.log('Final sketchFile after confirmation:', sketchFile);
      console.log('Final sceneDescription after confirmation:', sceneDescription);
      console.log('Button should be enabled after confirmation:', !!(sketchFile && sceneDescription));
    }
  };

  const handleAddAnotherCharacterDecision = (addAnother) => {
    setShowAddAnotherCharacter(false);
    if (addAnother) {
      // Keep existing character selections and reset for new character
      setCharacterConfirmed(false);
      setSelectedCharacterFromLibrary(null);
      // Keep the character library open for additional selections
      setShowCharacterLibrary(true);
    } else {
      // Continue with scene conceptualization
      console.log('Continuing with scene conceptualization with characters:', sceneTags);
    }
  };

  // Handle character save and next step
  const handleCharacterSave = () => {
    if (selectedCharacterFromLibrary) {
      // Add selected character to the list
      setSelectedCharacters(prev => [...prev, selectedCharacterFromLibrary]);
      
      setSelectedCharacterFromLibrary(null);
      
      // Ask if user wants to add another character
      setShowAddAnotherCharacter(true);
    }
  };

  // Handle character removal
  const handleRemoveCharacter = (characterIndex) => {
    // Get the character tag to remove
    const characterTags = sceneTags.filter(tag => tag.characterData);
    const tagToRemove = characterTags[characterIndex];
    
    if (tagToRemove) {
      // Remove the tag from sceneTags
      setSceneTags(prev => prev.filter(tag => tag.id !== tagToRemove.id));
      
      // Also remove from storySetupData if it exists there
      if (storySetupData) {
        const updatedCharacters = storySetupData.characters.filter(char => char.name !== tagToRemove.characterData.name);
        const updatedStorySetupData = {
          ...storySetupData,
          characters: updatedCharacters
        };
        setStorySetupData(updatedStorySetupData);
      }
      
      console.log('Removed character:', tagToRemove.characterData.name);
    }
  };

  // Handle character deletion from library
  const handleDeleteCharacterFromLibrary = async (characterId, characterName) => {
    console.log('=== DELETE CHARACTER FROM LIBRARY ===');
    console.log('Character ID:', characterId);
    console.log('Character Name:', characterName);
    
    // Set the character to delete and show confirmation modal
    setCharacterToDelete({ id: characterId, name: characterName });
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteCharacter = async () => {
    if (!characterToDelete) return;
    
    const { id: characterId, name: characterName } = characterToDelete;
    
    try {
      console.log('Making DELETE request to:', `${API_ENDPOINTS.backendBase}/api/characters/${characterId}`);
      const response = await fetch(`${API_ENDPOINTS.backendBase}/api/characters/${characterId}`, {
        method: 'DELETE'
      });

      console.log('DELETE response status:', response.status);
      console.log('DELETE response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('DELETE response error:', errorText);
        throw new Error(`Failed to delete character: ${response.status} ${errorText}`);
      }

      // Remove the character from the local state
      setUserCharacters(prev => {
        const filtered = prev.filter(char => (char.id !== characterId && char.characterId !== characterId));
        console.log('Updated userCharacters:', filtered);
        return filtered;
      });
      
      // Also remove from characters state if it exists there
      setCharacters(prev => {
        const filtered = prev.filter(char => (char.id !== characterId && char.characterId !== characterId));
        console.log('Updated characters:', filtered);
        return filtered;
      });
      
      console.log('Character deleted successfully:', characterName);
      
      // Show success message with custom modal
      setCharacterToDelete({ id: null, name: characterName, success: true });
      setShowDeleteConfirmation(true);
      
    } catch (error) {
      console.error('Error deleting character:', error);
      // Show error message with custom modal
      setCharacterToDelete({ id: null, name: characterName, error: true });
      setShowDeleteConfirmation(true);
    }
  };

  // Handle object addition
  const handleAddObject = () => {
    if (artworkObject.name.trim()) {
      const tagId = uuidv4();
      const newObject = {
        id: tagId,
        name: artworkObject.name,
        brief: artworkObject.brief || '',
        x: 50,
        y: 50
      };
      setObjects(prev => [...prev, newObject]);
      
      // Create tag for the object
      const newTag = {
        id: tagId,
        name: artworkObject.name,
        x: 50,
        y: 50,
        objectData: {
          name: artworkObject.name,
          type: 'object',
          description: artworkObject.brief || '',
          reason: artworkObject.brief || ''
        }
      };
      setSceneTags(prev => [...prev, newTag]);
      
      // Prepare action binding for this new object and prompt user
      setPendingObjectTagId(tagId);
      setBindingDraft(d => ({ ...d, objectTagId: tagId }));
      // Immediately open action binding modal
      setShowActionBindingModal(true);
      
      // Reset form and ask if user wants to add another
      setArtworkObject({ name: '', brief: '' });
      setShowAddAnotherObject(true);
      
      console.log('Added object:', newObject);
    }
  };

  // Handle add another object decision
  const handleAddAnotherObjectDecision = (addAnother) => {
    setShowAddAnotherObject(false);
    if (addAnother) {
      // Reset form for another object
      setArtworkObject({ name: '', brief: '' });
    } else {
      // Continue with scene conceptualization
      console.log('Continuing with scene conceptualization with objects:', objects);
    }
  };

  // Handle object removal
  const handleRemoveObject = (objectIndex) => {
    const objectToRemove = objects[objectIndex];
    setObjects(prev => prev.filter((obj, idx) => idx !== objectIndex));
    
    // Also remove the corresponding tag
    setSceneTags(prev => prev.filter(tag => tag.objectData?.name !== objectToRemove.name));
  };

  // Handle sketch deletion with confirmation
  const handleDeleteSketch = () => {
    setShowDeleteSketchConfirm(true);
  };

  const confirmDeleteSketch = () => {
    setSketchUrl(null);
    setSketchFile(null);
    setSceneTags([]); // Also clear tags when sketch is deleted
    setShowDeleteSketchConfirm(false);
  };

  const cancelDeleteSketch = () => {
    setShowDeleteSketchConfirm(false);
  };

  // Handle action binding addition
  const handleAddActionBinding = () => {
    if (!bindingDraft.actorTagId || !bindingDraft.objectTagId || !bindingDraft.action.trim()) {
      console.warn('⚠️ Cannot add action binding - missing required fields:', {
        actorTagId: !!bindingDraft.actorTagId,
        objectTagId: !!bindingDraft.objectTagId,
        action: !!bindingDraft.action.trim()
      });
      return;
    }
    const actorTag = sceneTags.find(t => t.id === bindingDraft.actorTagId);
    const objectTag = sceneTags.find(t => t.id === bindingDraft.objectTagId);
    
    if (!actorTag || !objectTag) {
      console.error('❌ Cannot find tags for action binding:', {
        actorTag: !!actorTag,
        objectTag: !!objectTag,
        actorTagId: bindingDraft.actorTagId,
        objectTagId: bindingDraft.objectTagId,
        availableSceneTags: sceneTags.map(t => ({ id: t.id, name: t.name || t.characterData?.name || t.objectData?.name }))
      });
      return;
    }

    const newBinding = {
      actorTagId: bindingDraft.actorTagId,
      objectTagId: bindingDraft.objectTagId,
      action: bindingDraft.action.trim(),
      relation: bindingDraft.relation || 'holding',
      grip: bindingDraft.grip || '',
      actorName: actorTag.characterData?.name || actorTag.name || 'character',
      objectName: objectTag.objectData?.name || objectTag.name || 'object'
    };

    console.log('✅ Adding action binding:', newBinding);
    setActionBindings(prev => {
      const updated = [...prev, newBinding];
      console.log('📋 Updated actionBindings:', updated.length, 'total bindings');
      return updated;
    });
    setBindingDraft({
      actorTagId: '',
      objectTagId: '',
      action: '',
      relation: 'holding',
      grip: ''
    });
  };

  // Handle action binding removal
  const handleRemoveActionBinding = (index) => {
    setActionBindings(prev => prev.filter((_, i) => i !== index));
  };

  const objectForActionBinding = pendingObjectTagId
    ? sceneTags.find(t => t.id === pendingObjectTagId)
    : null;

  const renderConceptualizationStep = () => (
    <div
      className="relative flex h-screen min-h-screen flex-col overflow-hidden px-0 py-0 md:px-[2.5cm] md:py-6"
      style={atmospherePremiumPageStyle}
    >
      <ArtmospherePremiumBackground variant="aurora" intensity={0.38} />

      {/* Main Content — full bleed on mobile, 2cm left/right on desktop */}
      <div className="relative z-10 min-h-screen min-h-[100dvh] flex items-center justify-center p-0 md:px-[2cm] md:py-4 overflow-hidden">
        {/* Sleek sophisticated border frame around entire conceptualization window */}
        <div className="w-full max-w-full md:max-w-[calc(100%-0px)] h-[100dvh] md:h-auto md:max-h-[95vh] rounded-none md:rounded-2xl p-[2px] bg-gradient-to-br from-amber-400/90 via-amber-300/70 to-amber-200/80 shadow-[0_0_0_1px_rgba(180,83,9,0.25),0_4px_24px_rgba(180,130,70,0.12),0_12px_40px_rgba(180,130,70,0.08)]">
          <div className="bg-white rounded-none md:rounded-[calc(1rem-2px)] w-full h-full overflow-hidden flex flex-col min-h-0 shadow-[inset_0_0_0_1px_rgba(251,191,36,0.12)]">
            {/* Compact header — light amber top area */}
          <div className="flex-shrink-0 bg-amber-50 border-b border-amber-200/80 shadow-sm">
            <div className="flex items-center justify-between py-2.5 md:py-3 px-4 md:px-6 min-h-[52px]">
              <button
                onClick={handleBackNavigation}
                className="flex items-center space-x-1 md:space-x-2 text-amber-800 hover:text-amber-900 font-semibold py-1.5 md:py-2 px-2 md:px-4 rounded-lg transition-colors text-xs md:text-base"
              >
                <span>←</span>
                <span className="hidden md:inline">Back</span>
              </button>
              <div className="text-center flex-1 min-w-0 py-1.5 md:py-2 px-2">
                <h2 className="text-base md:text-lg font-semibold text-gray-800 truncate">Scene {currentScene} Conceptualization</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {conceptualizationStep === 1 && "Step 1: Character Details & Tagging"}
                  {conceptualizationStep === 2 && "Step 2: Scene Description & Sketch Upload"}
                  {conceptualizationStep === 3 && "Step 3: Validation & Confirmation"}
                </p>
              </div>
              <button
                className="text-gray-500 hover:text-gray-700 text-xl md:text-2xl p-2 -m-2"
                onClick={() => {
                  const targetScene = Math.max(1, (currentScene || 1) - 1);
                  if (storyId) {
                    navigate(`/view-book/${storyId}?scene=${targetScene}`);
                  } else {
                    navigate('/storyboarding-landing');
                  }
                }}
                title="Close"
              >
                ×
              </button>
            </div>
            <div className="h-1 bg-gray-100 overflow-hidden">
              <div className="h-full bg-slate-500 transition-all duration-300" style={{ width: `${(conceptualizationStep / 3) * 100}%` }} />
            </div>
          </div>

          <div className="flex flex-col lg:flex-row h-full overflow-y-auto lg:overflow-hidden flex-1 min-h-0 min-w-0">
            {/* Left: Sketch Upload */}
            <div className="w-full lg:w-1/2 min-w-0 p-3 md:p-6 bg-gray-50 lg:overflow-y-auto flex-shrink-0 flex flex-col items-center">
              <div className="mb-3 md:mb-4 w-full">
                <h4 className="text-sm md:text-lg font-semibold text-gray-800 mb-1 md:mb-2 text-center">
                  Your Scene Sketch
                </h4>
                <p className="text-xs md:text-sm text-gray-600 hidden md:block text-center">
                  {conceptualizationStep === 1 && "Let's add character details and tag them on your sketch."}
                  {conceptualizationStep === 2 && "Now describe the scene and select the genre."}
                  {conceptualizationStep === 3 && "Perfect! Review your scene details and get ready to generate your artwork."}
                </p>
              </div>

              {/* Sketch window with golden Artmosphere frame — image fits to scale */}
              <div className="bg-gradient-to-br from-amber-200 via-amber-100 to-amber-50 rounded-2xl md:rounded-[1.75rem] p-[3px] shadow-[0_18px_45px_rgba(191,148,77,0.35)] border border-amber-200/70 mx-auto w-full" style={{ maxWidth: typeof window !== 'undefined' && window.innerWidth >= 1024 ? '90%' : '100%' }}>
                <div className="bg-white rounded-xl md:rounded-2xl overflow-hidden relative shadow-inner flex items-center justify-center min-h-[200px]" style={{ maxHeight: typeof window !== 'undefined' && window.innerWidth < 1024 ? '50vh' : '75vh' }}>
                  {sketchUrl ? (
                    <>
                      <div className="relative inline-block max-w-full" style={{ maxHeight: typeof window !== 'undefined' && window.innerWidth < 1024 ? '50vh' : '75vh' }}>
                        <img
                          src={sketchUrl}
                          alt="Your Sketch"
                          className="max-w-full max-h-[50vh] md:max-h-[75vh] w-auto h-auto object-contain align-top block"
                          style={{ maxWidth: '100%', verticalAlign: 'top' }}
                        />
                        {/* Character Tag Overlay - same wrapper as image so coordinates match */}
                        <CharacterTagOverlay
                          artworkUrl={sketchUrl}
                          taggedCharacters={sceneTags}
                          showTags={true}
                          draggable={true}
                          onTagPositionChange={handleTagPositionChange}
                        />
                      </div>
                      {/* Delete Sketch Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSketch();
                        }}
                        className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg transition-all duration-200 z-10 flex items-center justify-center"
                        title="Delete sketch"
                      >
                        <FaTrash className="text-sm" />
                      </button>
                    </>
                  ) : (
                    <div
                      className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-amber-50 transition-colors p-8"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <FaUpload className="text-5xl md:text-6xl text-slate-500 mb-4" />
                      <p className="text-gray-700 text-center text-base md:text-lg font-medium">Click to upload your sketch</p>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </div>
              </div>

              {/* Scene Brief - Mandatory - Moved under sketch upload */}
              {conceptualizationStep === 1 && (
                <div className="mt-3 md:mt-4 w-full">
                  <label className="block text-xs md:text-sm font-semibold text-gray-800 mb-1.5 md:mb-2">
                    Scene Brief (Required) 
                    <span className="text-red-500">*</span>
                    <span className="text-xs text-gray-600 hidden md:inline"> (One line description of what the scene is about)</span>
                  </label>
                  <input
                    type="text"
                    maxLength={100}
                    className="w-full border border-gray-200 rounded-lg p-2 md:p-2.5 text-xs md:text-base focus:border-slate-400 focus:outline-none text-gray-800 bg-white"
                    value={sceneBrief}
                    onChange={(e) => setSceneBrief(e.target.value)}
                    placeholder="E.g. A girl sitting on a bench watching a horse in a fenced pasture"
                    required
                  />
                  <div className="text-xs text-gray-600 mt-1 md:mt-1.5">
                    {sceneBrief.length}/100 characters
                  </div>
                </div>
              )}
            </div>

            {/* Right: Forms */}
            <div className="w-full lg:w-1/2 min-w-0 p-2 md:p-6 pb-2 md:pb-8 bg-gray-50 border-t lg:border-t-0 lg:border-l border-gray-200 lg:overflow-y-auto flex-1 min-h-0">
              {/* Step 2 (now 1): Character Details & Tagging */}
              {conceptualizationStep === 1 && (
                <div className="space-y-3 md:space-y-6">
                  <h3 className="text-base md:text-xl font-bold text-gray-800 mb-2 md:mb-4">Step 1: Character Details & Tagging</h3>
                  
                  {/* Character type selection */}
                  <div className="mb-3 md:mb-6">
                    <label className="block text-xs md:text-sm font-semibold text-gray-800 mb-2 md:mb-3">
                      What is this scene about?
                    </label>
                    <div className="flex flex-wrap gap-1.5 md:gap-2 mb-2 md:mb-4">
                      <label className="flex items-center gap-1 md:gap-2 bg-white px-2 md:px-4 py-1.5 md:py-2 rounded-lg border border-gray-200 hover:border-slate-400 transition-colors cursor-pointer flex-1 min-w-0">
                        <input 
                          type="radio" 
                          name="artworkType" 
                          value="people" 
                          checked={artworkType === 'people'} 
                          onChange={() => setArtworkType('people')} 
                          className="text-gray-600"
                        />
                        <span className="font-medium text-gray-800 text-xs md:text-sm">Characters</span>
                      </label>
                      <label className="flex items-center gap-1 md:gap-2 bg-white px-2 md:px-4 py-1.5 md:py-2 rounded-lg border border-gray-200 hover:border-slate-400 transition-colors cursor-pointer flex-1 min-w-0">
                        <input 
                          type="radio" 
                          name="artworkType" 
                          value="object" 
                          checked={artworkType === 'object'} 
                          onChange={() => setArtworkType('object')} 
                          className="text-gray-600"
                        />
                        <span className="font-medium text-gray-800 text-xs md:text-sm">Scene Items</span>
                      </label>
                      {/* Scene/Object Library Button */}
                      <button
                        type="button"
                        onClick={() => setShowSceneObjectLibrary(true)}
                        className="w-full md:w-auto bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300 px-2 md:px-3 py-1.5 md:py-1 rounded-lg text-xs md:text-sm font-semibold transition-colors"
                      >
                        Scene/Object Library
                      </button>
                    </div>
                  </div>

                  {/* Character forms based on type */}
                  {artworkType === 'people' && (
                    <div className="space-y-6">
                      <div className="mb-4">
                        <h4 className="text-lg font-semibold text-gray-800 mb-2">Who's in This Scene?</h4>
                        <p className="text-sm text-gray-600">Select characters and position your tag on the character from your story or add new ones!</p>
                      </div>
                      
                      {/* Character Creation Section */}
                      <div className="bg-white rounded-lg md:rounded-xl p-3 md:p-6 border border-gray-200 shadow-lg">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-3 md:mb-4 gap-2 md:gap-0">
                          <h4 className="font-bold text-gray-800 text-sm md:text-lg flex items-center">
                            <FaUser className="mr-1 md:mr-2 text-gray-600 text-xs md:text-base" />
                            <span className="text-xs md:text-base">{storySetupData && storySetupData.characters.length > 0 ? 'Characters from Your Story' : 'Create Characters for Your Scene'}</span>
                          </h4>
                          <div className="flex flex-col md:flex-row gap-2 md:space-x-2">
                            <button
                              onClick={() => {
                                console.log('=== MY CHARACTER LIBRARY BUTTON CLICKED ===');
                                console.log('Loading user character library...');
                                loadUserCharacterLibrary();
                                console.log('Setting showCharacterLibrary to true');
                                setShowCharacterLibrary(true);
                                console.log('showCharacterLibrary should now be true');
                              }}
                              className="bg-amber-100 hover:bg-amber-200 text-amber-900 font-semibold py-1.5 md:py-2 px-2 md:px-3 rounded-lg transition-colors flex items-center justify-center space-x-1 md:space-x-2 text-xs"
                            >
                              <FaBook className="text-xs" />
                              <span>My Character Library</span>
                            </button>
                            <button
                              onClick={() => {
                                console.log('=== ADD NEW CHARACTER BUTTON CLICKED ===');
                                console.log('Setting showEnhancedCharacterModal to true');
                                setShowEnhancedCharacterModal(true);
                                console.log('showEnhancedCharacterModal should now be true');
                              }}
                              className="bg-amber-500 hover:bg-amber-600 text-white font-semibold py-1.5 md:py-2 px-2 md:px-3 rounded-lg transition-colors flex items-center justify-center space-x-1 md:space-x-2 text-xs"
                            >
                              <FaPlus className="text-xs" />
                              <span>Add New Character</span>
                            </button>
                          </div>
                        </div>
                        
                        {/* Show selected characters */}
                        {sceneTags.filter(tag => tag.characterData).length > 0 ? (
                          <div className="grid grid-cols-1 gap-3 max-h-48 overflow-y-auto">
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="font-semibold text-green-800">Selected Characters ({sceneTags.filter(tag => tag.characterData).length}):</h5>
                              <button
                                onClick={() => {
                                  setSceneTags(prev => prev.filter(tag => !tag.characterData));
                                  if (storySetupData) {
                                    const updatedStorySetupData = {
                                      ...storySetupData,
                                      characters: []
                                    };
                                    setStorySetupData(updatedStorySetupData);
                                  }
                                }}
                                className="text-red-500 hover:text-red-700 text-sm font-medium px-2 py-1 rounded hover:bg-red-50 transition-colors"
                              >
                                🗑️ Remove All
                              </button>
                            </div>
                            {sceneTags.filter(tag => tag.characterData).map((tag, idx) => (
                              <div
                                key={`scene-${tag.id}`}
                                className="p-4 rounded-lg border-2 border-green-300 bg-green-50 cursor-default transition-all"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-3">
                                    <div className="w-10 h-10 bg-green-200 rounded-full flex items-center justify-center">
                                      <FaUser className="text-green-600" />
                                    </div>
                                    <div>
                                      <div className="font-bold text-green-800">{tag.characterData.name}</div>
                                      <div className="text-sm text-green-600">{tag.characterData.age} years old, {tag.characterData.gender} • {tag.characterData.role}</div>
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <div className="text-green-500 text-xl">✓</div>
                                    <button
                                      onClick={() => handleRemoveCharacter(idx)}
                                      className="text-red-500 hover:text-red-700 text-sm font-medium p-2 rounded-full hover:bg-red-50 transition-colors"
                                      title={`Remove ${tag.characterData.name}`}
                                    >
                                      🗑️
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                            <div className="text-center p-3 bg-green-50 border-2 border-green-200 rounded-lg">
                              <p className="text-sm text-green-600">All characters are ready for scene generation!</p>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-4 md:py-6">
                            <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg md:rounded-xl p-4 md:p-6">
                              <p className="text-sm md:text-base text-gray-700 font-medium">No characters added yet. Use the "Add New Character" button above to get started.</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {artworkType === 'object' && (
                    <div className="space-y-4">
                      <div className="text-gray-700 font-semibold mb-2">
                        Tell us about the objects, animals, and things in your scene!
                      </div>
                      
                      {/* Object Input Form */}
                      <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-lg">
                        <div className="mb-2">
                          <label className="block text-sm font-semibold text-gray-800">What's the name of this thing?</label>
                          <input 
                            type="text" 
                            value={artworkObject.name} 
                            onChange={e => setArtworkObject({ ...artworkObject, name: e.target.value })} 
                            className="w-full border border-gray-200 rounded-lg p-2 focus:border-slate-400 focus:outline-none text-gray-800 bg-white" 
                            placeholder="Enter name (e.g., cat, tree, toy, etc.)..."
                          />
                        </div>
                        
                        {/* Add Scene Item Button */}
                        <button
                          onClick={handleAddObject}
                          disabled={!artworkObject.name.trim()}
                          className={`w-full mt-3 py-2.5 px-4 rounded-lg font-semibold text-sm md:text-base transition-colors ${
                            artworkObject.name.trim()
                              ? 'bg-amber-500 hover:bg-amber-600 text-white'
                              : 'bg-amber-200 text-amber-600 cursor-not-allowed'
                          }`}
                        >
                          ➕ Add Scene Item
                        </button>
                        
                        {/* Clear Form Button */}
                        {artworkObject.name.trim() && (
                          <button
                            onClick={() => setArtworkObject({ name: '', brief: '' })}
                            className="w-full mt-2 py-2 px-4 rounded-lg font-semibold text-sm md:text-base transition-colors bg-amber-100 hover:bg-amber-200 text-amber-900"
                          >
                            🗑️ Clear Form
                          </button>
                        )}
                      </div>
                      
                      {/* Manual tag creation instruction */}
                      {artworkObject.name.trim() && (
                        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3 mt-3">
                          <div className="flex items-center text-blue-700">
                            <span className="font-medium">Click "Add Scene Item" to create a tag for "{artworkObject.name}"</span>
                          </div>
                          <p className="text-sm text-blue-600 mt-1">Tags are only created when you manually add scene items.</p>
                        </div>
                      )}
                      
                      {/* Display Added Scene Items */}
                      {objects.length > 0 && (
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h5 className="font-semibold text-gray-800">Added Scene Items ({objects.length}):</h5>
                            <button
                              onClick={() => {
                                setObjects([]);
                                setSceneTags(prev => prev.filter(tag => !tag.objectData));
                                setActionBindings([]);
                              }}
                              className="text-red-500 hover:text-red-700 text-sm font-semibold px-2 py-1 rounded hover:bg-red-50 transition-colors"
                            >
                              🗑️ Remove All
                            </button>
                          </div>
                          <div className="space-y-2">
                            {objects.map((obj, index) => (
                              <div key={obj.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3">
                                <div className="flex items-center space-x-3">
                                  <div className="w-8 h-8 bg-slate-500 rounded-lg flex items-center justify-center shadow-md">
                                    <div className="w-4 h-4 bg-white rounded-sm transform rotate-45"></div>
                                  </div>
                                  <div>
                                    <div className="font-semibold text-gray-800">{obj.name}</div>
                                    <div className="text-sm text-gray-600">{obj.brief}</div>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleRemoveObject(index)}
                                  className="text-red-500 hover:text-red-700 text-lg font-bold p-2 rounded-full hover:bg-red-50 transition-colors"
                                  title={`Remove ${obj.name}`}
                                >
                                  🗑️
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                    </div>
                  )}
                  
                </div>
              )}


              {/* Step 2 (now 2): Scene Description & Sketch Upload */}
              {conceptualizationStep === 2 && (
                <div className="space-y-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Step 2: Scene Description & Sketch Upload</h3>
                  
                  {/* Scene Description */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      Let the author in you write your scene for this image?
                      <span className="text-xs text-gray-600"> think about the story behind the scene, basis the characters , scene elements identfied for this scene</span>
                    </label>
                    <textarea
                      className="w-full border border-gray-200 rounded-lg p-2 focus:border-slate-400 focus:outline-none text-gray-800 bg-white"
                      value={sceneDescription}
                      onChange={(e) => setSceneDescription(e.target.value)}
                      placeholder="E.g. The princess is dancing at the castle party, the dragon is flying over the mountains, etc."
                      rows="4"
                    />
                  </div>

                  {/* Scene Genre Selection */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      🎭 What's the genre of this scene?
                    </label>
                    <select
                      value={sceneGenre}
                      onChange={(e) => setSceneGenre(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg p-2 focus:border-slate-400 focus:outline-none text-gray-800 bg-white"
                    >
                      <option value="">Select a genre...</option>
                      <option value="Fantasy">Fantasy</option>
                      <option value="Adventure">Adventure</option>
                      <option value="Comedy">Comedy</option>
                      <option value="Drama">Drama</option>
                      <option value="Mystery">Mystery</option>
                      <option value="Romance">Romance</option>
                      <option value="Sci-Fi">Sci-Fi</option>
                      <option value="Horror">Horror</option>
                      <option value="Action">Action</option>
                      <option value="Slice of Life">Slice of Life</option>
                      <option value="Historical">Historical</option>
                      <option value="Educational">Educational</option>
                    </select>
                  </div>

                  {/* Removed debug controls */}
                  <div className="hidden" />
                </div>
              )}

              {/* Step 3: Validation & Confirmation */}
              {conceptualizationStep === 3 && (
                <div className="space-y-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Step 3: Validation & Confirmation</h3>
                  
                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg">
                    <h4 className="font-bold text-gray-800 text-lg mb-4">Scene Summary</h4>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-1">Scene Description:</label>
                        <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{sceneDescription || 'Not provided'}</p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-800 mb-1">Medium:</label>
      
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-800 mb-1">Genre:</label>
                          <p className="text-gray-700 bg-gray-50 p-2 rounded-lg">{sceneGenre || 'Not selected'}</p>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-1">Focus:</label>
                        <p className="text-gray-700 bg-gray-50 p-2 rounded-lg capitalize">{artworkType || 'Not selected'}</p>
                      </div>
                      
                      {sceneTags.length > 0 && (
                        <div>
                          <label className="block text-sm font-semibold text-gray-800 mb-1">Selected Characters:</label>
                          <div className="bg-gray-50 p-3 rounded-lg">
                            {sceneTags.map((tag, index) => (
                              <div key={tag.id} className="text-gray-700">
                                • {tag.name} ({tag.role})
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                    <h4 className="font-bold text-blue-800 mb-2">Ready to Generate?</h4>
                    <p className="text-blue-700 text-sm">
                      Make sure you've uploaded your sketch and filled in all the details above. 
                      Click "Generate Scene" to create your enhanced artwork!
                    </p>
                    
                    {/* Background Generation Status */}
                    {sceneConceptualizationInProgress && !refinedImageUrl && (
                      <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-500"></div>
                          <span className="text-green-700 text-sm font-medium">
                            Scene is being generated in the background...
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {refinedImageUrl && (
                      <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <span className="text-green-600">✅</span>
                          <span className="text-green-700 text-sm font-medium">
                            Scene generated successfully! Click "View Generated Scene" to see the result.
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Navigation and Action Buttons */}
              <div className="mt-3 md:mt-8 mb-3 md:mb-8 text-center">
                {/* Step indicator */}
                <div className="mb-4">
                  <div className="flex justify-center space-x-2">
                    {[1, 2, 3].map((step) => (
                      <div
                        key={step}
                        className={`w-3 h-3 rounded-full ${
                          step <= conceptualizationStep
                            ? 'bg-amber-500'
                            : 'bg-amber-200'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Step {conceptualizationStep} of 3
                  </p>
                </div>

                {/* Navigation Buttons */}
                <div className="flex justify-between items-center gap-2">
                  <button
                    onClick={handlePreviousConceptualizationStep}
                    disabled={conceptualizationStep === 1}
                    className={`flex-1 px-3 md:px-6 py-2.5 md:py-3 rounded-lg md:rounded-xl font-semibold text-xs md:text-base transition-colors flex items-center justify-center space-x-1 md:space-x-2 ${
                      conceptualizationStep === 1
                        ? 'bg-amber-200 text-amber-600 cursor-not-allowed'
                        : 'bg-amber-100 hover:bg-amber-200 text-amber-900'
                    }`}
                  >
                    <span>←</span>
                    <span>Previous</span>
                  </button>

                  {/* Center button changes based on step */}
                  {conceptualizationStep < 3 ? (
                    <button
                      onClick={handleNextConceptualizationStep}
                      disabled={!canProceedToNextStep()}
                      className={`flex-1 px-3 md:px-8 py-2.5 md:py-4 rounded-lg md:rounded-xl font-semibold text-xs md:text-lg transition-colors ${
                        !canProceedToNextStep()
                          ? 'bg-amber-200 text-amber-600 cursor-not-allowed'
                          : 'bg-amber-500 hover:bg-amber-600 text-white'
                      }`}
                    >
                      {conceptualizationStep === 2 ? 'Conceptualise Scene' : 'Next →'}
                    </button>
                  ) : (
                    <button
                      onClick={handleStep3GenerateScene}
                      disabled={sceneConceptualizationInProgress && !refinedImageUrl}
                      className={`flex-1 px-3 md:px-8 py-2.5 md:py-4 rounded-lg md:rounded-xl font-semibold text-xs md:text-lg transition-colors ${
                        sceneConceptualizationInProgress && !refinedImageUrl
                          ? 'bg-amber-200 text-amber-600 cursor-not-allowed'
                          : 'bg-amber-500 hover:bg-amber-600 text-white'
                      }`}
                    >
                      {sceneConceptualizationInProgress && !refinedImageUrl ? (
                        <div className="flex items-center justify-center space-x-1 md:space-x-2">
                          <div className="animate-spin rounded-full h-3 w-3 md:h-5 md:w-5 border-b-2 border-white"></div>
                          <span className="text-xs md:text-base">Generating...</span>
                        </div>
                      ) : refinedImageUrl ? (
                        'View Scene'
                      ) : (
                        'Generate Scene'
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Tag Placement Confirmation Modal */}
      {showTagPlacementConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Confirm Tag Placement</h3>
              <p className="text-gray-600 mb-6">
                Hey kids, ensure you have placed the tags on the sketch at the exact position where characters are required?
              </p>
              <div className="flex flex-col md:flex-row gap-3 md:gap-4">
                <button
                  onClick={handleTagPlacementCancel}
                  className="flex-1 bg-amber-100 hover:bg-amber-200 text-amber-900 font-semibold py-3 px-6 rounded-lg md:rounded-xl transition-colors text-sm md:text-base"
                >
                  Go Back
                </button>
                <button
                  onClick={handleTagPlacementConfirmation}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-semibold py-3 px-6 rounded-lg md:rounded-xl transition-colors text-sm md:text-base"
                >
                  Confirm & Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Binding Modal for newly added scene item */}
      {showActionBindingModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-gray-50 border border-gray-200 rounded-2xl shadow-2xl w-full max-w-3xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">
                Link an action to this scene item
              </h3>
              <button
                onClick={() => {
                  setShowActionBindingModal(false);
                  setPendingObjectTagId(null);
                  setBindingDraft({
                    actorTagId: '',
                    objectTagId: '',
                    action: '',
                    relation: 'holding',
                    grip: ''
                  });
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <p className="text-sm text-gray-700">
              {objectForActionBinding
                ? `You just added "${objectForActionBinding.objectData?.name || objectForActionBinding.name}". Tell us who is using it so the AI keeps the scene consistent.`
                : 'Tell us who is using which object so the AI keeps the scene consistent.'}
            </p>

            {characterTagsForBindings.length === 0 || objectTagsForBindings.length === 0 ? (
              <p className="text-sm text-red-600">
                Add at least one character and one scene item tag on the sketch to create an action binding.
              </p>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-800 mb-1">Who?</label>
                    <select
                      className="w-full border border-gray-200 rounded-lg p-2 text-gray-800 bg-white focus:border-slate-400 focus:outline-none"
                      value={bindingDraft.actorTagId}
                      onChange={e => setBindingDraft(d => ({ ...d, actorTagId: e.target.value }))}
                    >
                      <option value="">Choose character</option>
                      {characterTagsForBindings.map(tag => (
                        <option key={tag.id} value={tag.id}>
                          {tag.characterData?.name || tag.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-800 mb-1">Which object?</label>
                    <select
                      className="w-full border border-gray-200 rounded-lg p-2 text-gray-800 bg-white focus:border-slate-400 focus:outline-none"
                      value={bindingDraft.objectTagId}
                      onChange={e => setBindingDraft(d => ({ ...d, objectTagId: e.target.value }))}
                    >
                      <option value="">Choose scene item</option>
                      {objectTagsForBindings.map(tag => (
                        <option key={tag.id} value={tag.id}>
                          {tag.objectData?.name || tag.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-800 mb-1">What are they doing?</label>
                    <input
                      type="text"
                      className="w-full border border-gray-200 rounded-lg p-2 text-gray-800 bg-white focus:border-slate-400 focus:outline-none"
                      placeholder="e.g., playing, holding, singing into"
                      value={bindingDraft.action}
                      onChange={e => setBindingDraft(d => ({ ...d, action: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowActionBindingModal(false);
                      setPendingObjectTagId(null);
                      setBindingDraft({
                        actorTagId: '',
                        objectTagId: '',
                        action: '',
                        relation: 'holding',
                        grip: ''
                      });
                    }}
                    className="px-4 py-2 rounded-lg text-sm font-semibold bg-amber-100 hover:bg-amber-200 text-amber-900"
                  >
                    No action required for this scene item
                  </button>
                  <button
                    onClick={() => {
                      handleAddActionBinding();
                      setShowActionBindingModal(false);
                      setPendingObjectTagId(null);
                      setBindingDraft({
                        actorTagId: '',
                        objectTagId: '',
                        action: '',
                        relation: 'holding',
                        grip: ''
                      });
                    }}
                    disabled={
                      !bindingDraft.actorTagId ||
                      !bindingDraft.objectTagId ||
                      !bindingDraft.action.trim()
                    }
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 ${
                      bindingDraft.actorTagId &&
                      bindingDraft.objectTagId &&
                      bindingDraft.action.trim()
                        ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-md hover:shadow-lg'
                        : 'bg-amber-200 text-amber-600 cursor-not-allowed'
                    }`}
                  >
                    Link Action
                  </button>
                </div>
                {actionBindings.length > 0 && (
                  <div className="bg-white/80 border border-gray-200 rounded-lg p-3">
                    <h6 className="text-sm font-semibold text-gray-800 mb-2">Linked actions for this scene</h6>
                    <ul className="space-y-1 text-sm text-gray-700">
                      {actionBindings.map((b, index) => (
                        <li key={`${b.actorTagId}-${b.objectTagId}-${index}`} className="flex items-center justify-between">
                          <span>
                            <span className="font-semibold">{b.actorName}</span> is{' '}
                            <span className="font-semibold">{b.action}</span> the{' '}
                            <span className="font-semibold">{b.objectName}</span>
                          </span>
                          <button
                            onClick={() => handleRemoveActionBinding(index)}
                            className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50"
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Delete Sketch Confirmation Modal */}
      {showDeleteSketchConfirm && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <FaTrash className="text-red-600 text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Sketch?</h3>
              <p className="text-gray-600 text-sm">
                Are you sure you want to delete this sketch? This action cannot be undone and will also remove all tags placed on it.
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelDeleteSketch}
                className="px-6 py-2.5 rounded-lg font-semibold text-sm bg-amber-100 hover:bg-amber-200 text-amber-900 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteSketch}
                className="px-6 py-2.5 rounded-lg font-semibold text-sm bg-red-600 hover:bg-red-700 text-white transition-colors shadow-md"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BFL Details Modal */}
      {showBFLDetailsModal && bflDetails && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl md:rounded-2xl shadow-2xl max-w-4xl w-full mx-4 p-4 md:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg md:text-xl font-bold text-purple-800">BFL Generation Details</h3>
              <button
                onClick={() => setShowBFLDetailsModal(false)}
                className="text-gray-500 hover:text-gray-700 text-xl md:text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              {/* BFL Prompt */}
              {bflDetails.prompt && (
                <div>
                  <h4 className="text-base font-semibold text-purple-700 mb-2">BFL Prompt:</h4>
                  <div className="bg-gray-50 rounded-lg p-3 md:p-4 border border-gray-200">
                    <pre className="text-xs md:text-sm text-gray-800 whitespace-pre-wrap break-words">
                      {bflDetails.prompt}
                    </pre>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Length: {bflDetails.prompt.length} characters | 
                    Estimated Tokens: {Math.ceil(bflDetails.prompt.length / 4)}
                  </p>
                </div>
              )}
              
              {/* Aspect Ratio */}
              {bflDetails.aspectRatio && (
                <div>
                  <h4 className="text-base font-semibold text-purple-700 mb-2">Aspect Ratio:</h4>
                  <p className="text-sm text-gray-700">{bflDetails.aspectRatio}</p>
                </div>
              )}
              
              {/* Images sent to BFL */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Input Image (Character Image) */}
                {bflDetails.inputImage && (
                  <div>
                    <h4 className="text-base font-semibold text-purple-700 mb-2">
                      Input Image {bflDetails.mergedCharacterImageUrl && bflDetails.mergedCharacterImageUrl !== bflDetails.inputImageUrl ? '(Merged Characters)' : '(Character)'}:
                    </h4>
                    {bflDetails.mergedCharacterImageUrl && bflDetails.mergedCharacterImageUrl !== bflDetails.inputImageUrl && (
                      <div className="mb-2 p-2 bg-purple-50 rounded border border-purple-200">
                        <p className="text-xs font-semibold text-purple-800 mb-1">🎨 Merged Character Image URL:</p>
                        <p className="text-xs text-purple-700 break-all">{bflDetails.mergedCharacterImageUrl}</p>
                        <p className="text-xs text-purple-600 mt-1 italic">This is the merged character image sent to BFL</p>
                      </div>
                    )}
                    {bflDetails.inputImageUrl && (
                      <p className="text-xs text-gray-500 mb-2 break-all">URL: {bflDetails.inputImageUrl}</p>
                    )}
                    <div className="bg-gray-100 rounded-lg p-2 border border-gray-200">
                      <img 
                        src={bflDetails.inputImage} 
                        alt="BFL Input Image (Character)" 
                        className="w-full rounded-lg max-h-64 object-contain"
                      />
                    </div>
                  </div>
                )}
                
                {/* Control Image (Sketch) */}
                {bflDetails.controlImage && (
                  <div>
                    <h4 className="text-base font-semibold text-purple-700 mb-2">Control Image (Sketch):</h4>
                    <div className="bg-gray-100 rounded-lg p-2 border border-gray-200">
                      <img 
                        src={bflDetails.controlImage} 
                        alt="BFL Control Image (Sketch)" 
                        className="w-full rounded-lg max-h-64 object-contain"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <button
              onClick={() => setShowBFLDetailsModal(false)}
              className="w-full mt-6 bg-purple-500 hover:bg-purple-600 text-white font-semibold py-2.5 md:py-3 px-4 md:px-6 rounded-lg md:rounded-xl transition-all duration-300 text-sm md:text-base"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Scene Conceptualization Loader */}
      {showSceneConceptualizationLoader && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-white rounded-xl md:rounded-2xl p-4 sm:p-6 md:p-8 max-w-md w-full shadow-2xl text-center relative">
            {/* Close button - allow manual dismissal if stuck */}
            <button
              onClick={() => {
                console.log('⚠️ User manually dismissed conceptualization loader');
                setShowSceneConceptualizationLoader(false);
                setSceneConceptualizationInProgress(false);
                setSceneImageLoading(false);
                if (sceneConceptualizationAbortController) {
                  sceneConceptualizationAbortController.abort();
                  setSceneConceptualizationAbortController(null);
                }
              }}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 text-2xl font-bold"
              title="Cancel conceptualization"
            >
              ×
            </button>
            <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-3 md:mb-4">Artmosphere is conceptualising</h3>
            <p className="text-gray-600 mb-4 md:mb-6 text-sm md:text-base">
              Give a moment while Artmosphere is creating the scene based on your sketch...
            </p>
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-6 w-6 md:h-8 md:w-8 border-b-2 border-slate-500"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderSceneViewStep = () => {
    console.log('=== RENDERING SCENE VIEW ===');
    console.log('refinedImageUrl:', refinedImageUrl);
    console.log('currentScene:', currentScene);
    console.log('isArtworkExpanded:', isArtworkExpanded);
    
    return (
      <div className="relative min-h-screen overflow-hidden" style={atmospherePremiumPageStyle}>
        <ArtmospherePremiumBackground variant="aurora" intensity={0.38} />

        <div className="relative z-10 min-h-screen flex items-center justify-center p-3 md:px-[2cm] md:py-6">
          {/* Single window card - Activity Dashboard style with golden border */}
          <div className="w-full max-w-7xl mx-auto min-w-0">
            <div className="bg-gradient-to-br from-amber-200/40 via-amber-50/30 to-amber-100/40 rounded-2xl md:rounded-[1.75rem] p-[3px] shadow-[0_18px_45px_rgba(191,148,77,0.25)] border border-amber-200/70">
              <div className="bg-white rounded-xl md:rounded-2xl overflow-hidden flex flex-col min-h-[85vh] md:min-h-[82vh] shadow-inner">
                {/* Nav bar strip - buttons only */}
                <div className="flex-shrink-0 bg-gradient-to-r from-amber-500/10 to-orange-400/10 border-b border-amber-200/80">
                  <div className="flex flex-col gap-2 sm:gap-0 sm:flex-row sm:items-center sm:justify-between py-3 px-3 md:px-6">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={handleBackNavigation}
                        className="flex items-center text-amber-800 hover:text-amber-900 font-semibold py-1.5 px-2 md:px-3 rounded-lg transition-colors text-xs md:text-sm"
                      >
                        <FaArrowLeft className="mr-1" />
                        <span>Back</span>
                      </button>
                      {refinedImageUrl && (
                        <>
                          <button
                            onClick={handlePublishStory}
                            disabled={!storySaved || isSavingStory}
                            className={`font-semibold py-1.5 px-2 md:px-3 rounded-lg shadow transition-all text-xs md:text-sm whitespace-nowrap ${
                              !storySaved || isSavingStory ? 'bg-amber-200 text-amber-600 cursor-not-allowed' : 'bg-amber-500 hover:bg-amber-600 text-white'
                            }`}
                          >
                            Publish Story
                          </button>
                          <button
                            onClick={handleSaveStory}
                            disabled={isSavingStory}
                            className={`font-semibold py-1.5 px-2 md:px-3 rounded-lg shadow transition-all text-xs md:text-sm whitespace-nowrap ${
                              isSavingStory ? 'bg-amber-200 text-amber-600 cursor-not-allowed' : storySaved ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-amber-500 hover:bg-amber-600 text-white'
                            }`}
                          >
                            {isSavingStory ? 'Saving...' : 'Save Story'}
                          </button>
                          <button
                            onClick={handleBookView}
                            disabled={scenes.length === 0 && !refinedImageUrl}
                            className="font-semibold py-1.5 px-2 md:px-3 rounded-lg border-2 border-amber-400 bg-white text-amber-800 hover:bg-amber-50 transition-all text-xs md:text-sm whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed disabled:border-amber-200"
                          >
                            View My Book
                          </button>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                      <button
                        onClick={handleBackToConceptualization}
                        className="bg-amber-500 hover:bg-amber-600 text-white font-semibold py-1.5 px-2 md:px-3 rounded-lg shadow transition-colors text-xs md:text-sm whitespace-nowrap"
                      >
                        Back to Conceptualization
                      </button>
                      <button
                        onClick={() => navigate('/my-art-stories')}
                        className="bg-amber-500 hover:bg-amber-600 text-white font-semibold py-1.5 px-2 md:px-3 rounded-lg shadow transition-colors text-xs md:text-sm whitespace-nowrap"
                      >
                        My Art Stories
                      </button>
                    </div>
                  </div>
                </div>

                {/* Story header - below nav strip, center-aligned with padding */}
                <div className="flex-shrink-0 px-4 md:px-8 py-4 md:py-5 flex justify-center border-b border-amber-100">
                  <input
                    type="text"
                    value={storyTitle}
                    onChange={(e) => setStoryTitle(e.target.value)}
                    placeholder="Your story title..."
                    className="text-center text-lg md:text-xl lg:text-2xl font-bold text-gray-800 bg-white border-2 border-amber-200/80 rounded-xl shadow-sm px-6 md:px-8 py-2.5 md:py-3 w-full max-w-md focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 placeholder-gray-400"
                  />
                </div>

                {/* Main content - two columns */}
                <div className="flex-1 flex flex-col lg:flex-row gap-4 md:gap-6 p-4 md:p-6 overflow-y-auto min-h-0">
              {/* Left: Artwork column */}
              <div className="w-full lg:w-[480px] flex-shrink-0 flex flex-col items-center justify-start">
                <div className="relative w-full flex flex-col items-center">
                  {/* Scene count indicator - top left above artwork */}
                  <div className="absolute top-2 left-2 md:top-0 md:left-0 z-10 bg-white/90 backdrop-blur-sm rounded-lg px-2 md:px-3 py-1 md:py-2 shadow-lg">
                    <span className="text-gray-800 font-semibold text-xs md:text-sm">
                      Scene {currentScene}/15
                    </span>
                  </div>
                  
                  {/* Action buttons - Mobile Responsive */}
                  {refinedImageUrl && (
                    <div className="absolute top-2 right-2 md:top-4 md:right-4 flex flex-col sm:flex-row gap-1 sm:gap-2 z-50">
                      <button
                        onClick={() => {
                          console.log('=== EDIT BUTTON CLICKED ===');
                          console.log('Edit button clicked!');
                          console.log('refinedImageUrl:', refinedImageUrl);
                          console.log('currentScene:', currentScene);
                          console.log('sceneDescription:', sceneDescription);
                          handleSceneEdit({
                            imageUrl: refinedImageUrl,
                            title: `Scene ${currentScene}`,
                            description: sceneDescription,
                            sceneNumber: currentScene
                          });
                        }}
                        className="bg-amber-500 hover:bg-amber-600 text-white px-2 md:px-3 py-1.5 md:py-2 rounded-lg shadow-lg transition-all duration-300 text-xs md:text-sm font-semibold"
                        title="Edit Scene"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          console.log('=== EXPAND BUTTON CLICKED ===');
                          console.log('Expand button clicked!');
                          console.log('Current isArtworkExpanded:', isArtworkExpanded);
                          console.log('refinedImageUrl:', refinedImageUrl);
                          setIsArtworkExpanded(!isArtworkExpanded);
                          console.log('New isArtworkExpanded will be:', !isArtworkExpanded);
                        }}
                        className="bg-amber-500 hover:bg-amber-600 text-white p-1.5 md:p-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
                        title={isArtworkExpanded ? "Collapse" : "Expand"}
                      >
                        {isArtworkExpanded ? <FaCompress className="text-xs md:text-sm" /> : <FaExpand className="text-xs md:text-sm" />}
                      </button>
                    </div>
                  )}
                  
                  {refinedImageUrl ? (
                    <div className="relative w-full">
                      {/* Splash Image Overlay - Morphed onto scene display */}
                      <div 
                        className="absolute inset-0 -z-10 opacity-50"
                        style={{
                          backgroundImage: `url(${splashImage})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          backgroundRepeat: 'no-repeat',
                          mixBlendMode: 'multiply',
                          filter: 'brightness(1.05)',
                          transform: 'scaleX(1.2) scaleY(0.9)',
                          pointerEvents: 'none',
                          borderRadius: '0.75rem',
                        }}
                      />
                      {/* Determine aspect ratio based on number of characters */}
                      {(() => {
                        const characterCount = sceneTags.filter(tag => tag.characterData).length;
                        const aspectRatio = characterCount > 1 ? '16/9' : '1/1';
                        console.log(`Scene has ${characterCount} characters, using aspect ratio: ${aspectRatio}`);
                        
                        return (
                          <img 
                            key={refinedImageUrl || 'generated-scene'}
                            src={refinedImageUrl} 
                            alt="Generated Scene" 
                            className="w-full rounded-xl md:rounded-2xl shadow-lg border-2 md:border-4 border-gray-200 relative z-10"
                            style={{ 
                              aspectRatio: aspectRatio,
                              objectFit: 'contain',
                              maxHeight: typeof window !== 'undefined' && window.innerWidth < 1024 
                                ? (characterCount > 1 ? '50vh' : '45vh')
                                : (characterCount > 1 ? '70vh' : '60vh')
                            }}
                            onError={(e) => {
                              const errorInfo = {
                                url: refinedImageUrl,
                                timestamp: new Date().toISOString(),
                                naturalWidth: e.target.naturalWidth,
                                naturalHeight: e.target.naturalHeight,
                                complete: e.target.complete,
                                src: e.target.src
                              };
                              console.error('❌ IMAGE LOAD ERROR:', errorInfo);
                              
                              // Try to fetch the image directly to see what the actual error is
                              fetch(refinedImageUrl, { method: 'HEAD' })
                                .then(response => {
                                  console.error('❌ Image fetch response:', {
                                    status: response.status,
                                    statusText: response.statusText,
                                    headers: Object.fromEntries(response.headers.entries())
                                  });
                                })
                                .catch(fetchError => {
                                  console.error('❌ Image fetch error:', fetchError);
                                });
                              
                              // Show error overlay
                              e.target.style.border = '3px solid red';
                              e.target.alt = 'Failed to load image';
                              
                              // Remove any existing error message
                              const existingError = e.target.parentElement?.querySelector('.image-error-message');
                              if (existingError) {
                                existingError.remove();
                              }
                              
                              // Add error message overlay
                              if (e.target.parentElement) {
                                const errorDiv = document.createElement('div');
                                errorDiv.className = 'image-error-message absolute inset-0 bg-red-100/90 backdrop-blur-sm border-2 border-red-500 rounded-xl flex items-center justify-center z-10';
                                errorDiv.innerHTML = `
                                  <div class="text-center p-4 max-w-md">
                                    <p class="text-red-700 font-bold text-lg mb-2">⚠️ Image failed to load</p>
                                    <p class="text-red-600 text-sm mt-2 break-all">URL: ${refinedImageUrl}</p>
                                    <p class="text-red-500 text-xs mt-2">Status: ${errorInfo.complete ? 'Complete but failed' : 'Incomplete'}</p>
                                    <button 
                                      onclick="window.location.reload()" 
                                      class="mt-4 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-semibold"
                                    >
                                      Reload Page
                                    </button>
                                  </div>
                                `;
                                e.target.parentElement.appendChild(errorDiv);
                              }
                            }}
                            onLoad={(e) => {
                              console.log('Image loaded successfully:', refinedImageUrl);
                              console.log('Image dimensions:', e.target.naturalWidth, 'x', e.target.naturalHeight);
                            }}
                          />
                        );
                      })()}
                      {sceneImageLoading && (
                        <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex flex-col items-center justify-center rounded-xl md:rounded-2xl border-2 md:border-4 border-gray-200">
                          <div className="animate-spin rounded-full h-6 md:h-8 w-6 md:w-8 border-b-2 border-slate-500 mb-2 md:mb-3"></div>
                          <p className="text-gray-700 font-semibold text-center px-4 text-sm md:text-base">
                            Preparing illustrated scene...
                          </p>
                        </div>
                      )}
                      
                      {/* Upload Artwork Option */}
                      <div className="mt-4 w-full bg-white/90 backdrop-blur-sm border border-gray-200 rounded-xl p-4 md:p-6 shadow-lg relative overflow-hidden">
                        {/* Splash Image Overlay - Morphed onto upload section */}
                        <div 
                          className="absolute inset-0 opacity-45"
                          style={{
                            backgroundImage: `url(${splashImage})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            mixBlendMode: 'multiply',
                            filter: 'brightness(1.1)',
                            transform: 'scaleX(1.3) scaleY(0.85)',
                            pointerEvents: 'none',
                          }}
                        />
                        <div className="text-center mb-3 md:mb-4 relative z-10">
                          <h4 className="text-base md:text-lg font-bold text-gray-800 mb-2">Upload Your Artwork</h4>
                          <p className="text-xs md:text-sm text-gray-700 mb-3">
                            The created image is an inspiration for you. Feel free to use the same for your book or upload your completed scene artwork.
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            console.log('📤 Upload Artwork button clicked');
                            // Close expanded artwork view if open
                            if (isArtworkExpanded) {
                              setIsArtworkExpanded(false);
                            }
                            setShowUploadArtworkModal(true);
                            console.log('✅ Upload modal should now be open');
                          }}
                          className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold py-2.5 md:py-3 px-4 md:px-6 rounded-lg md:rounded-xl shadow-md hover:shadow-lg transition-all duration-300 text-sm md:text-base flex items-center justify-center space-x-2 relative z-20"
                        >
                          <FaUpload className="text-xs md:text-sm" />
                          <span>Upload Artwork</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      {sceneImageLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-500 mx-auto mb-4"></div>
                          <p className="text-gray-700 font-semibold">Preparing illustrated scene...</p>
                          <p className="text-gray-500 text-sm mt-2">Applying the Old French watercolor finish</p>
                        </>
                      ) : sceneConceptualizationInProgress ? (
                        <>
                          <p className="text-purple-600 text-lg mb-4">Recreating scene...</p>
                          <p className="text-purple-500 text-sm">Generating a new version with the same parameters</p>
                          <div className="mt-4 flex justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                          </div>
                        </>
                      ) : (
                        <>
                          <p className="text-gray-600 text-lg mb-4">No scene generated yet</p>
                          <p className="text-gray-500 text-sm">Upload a sketch and conceptualize the scene to see the result here</p>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Right: Story and Scene Management - Artmosphere Style */}
              <div className="w-full lg:flex-1 flex flex-col gap-6">
                {/* Current Scene Section */}
                <div className="bg-white/80 backdrop-blur-sm border border-gray-200 rounded-2xl p-6 shadow-lg relative overflow-hidden">
                  {/* Splash Image Overlay - Morphed onto scene details section */}
                  <div 
                    className="absolute inset-0 opacity-40"
                    style={{
                      backgroundImage: `url(${splashImage})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      mixBlendMode: 'multiply',
                      filter: 'brightness(1.1)',
                      transform: 'scaleX(1.25) scaleY(0.9)',
                      pointerEvents: 'none',
                    }}
                  />
                  <h3 className="text-xl font-bold text-gray-800 mb-4 relative z-10">Scene {currentScene}</h3>
                  
                  {/* Scene Title */}
                  <div className="mb-4 relative z-10">
                    <label className="block text-gray-700 font-semibold mb-2">Scene Title</label>
                    <input
                      type="text"
                      value={`Scene ${currentScene}`}
                      readOnly
                      className="w-full p-3 border border-gray-200 rounded-xl bg-gray-50 text-gray-700"
                    />
                  </div>

                  {/* Scene Description */}
                  <div className="mb-4 relative z-10">
                    <label className="block text-gray-700 font-semibold mb-2">Scene Description</label>
                    <textarea
                      value={sceneDescription}
                      onChange={(e) => setSceneDescription(e.target.value)}
                      placeholder="Describe what happens in this scene..."
                      className="w-full p-3 border border-gray-200 rounded-xl focus:border-slate-400 focus:outline-none min-h-[120px] resize-none text-gray-800 bg-white"
                    />
                  </div>

                  {/* Next Scene Button */}
                  <button
                    onClick={handleNextScene}
                    disabled={currentScene >= 15}
                    className={`w-full font-semibold py-2.5 md:py-3 px-4 md:px-6 rounded-lg md:rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center space-x-2 text-sm md:text-base relative z-10 ${
                      currentScene >= 15
                        ? 'bg-amber-200 text-amber-600 cursor-not-allowed'
                        : 'bg-amber-500 hover:bg-amber-600 text-white'
                    }`}
                  >
                    <span>Add Next Scene ({currentScene}/15)</span>
                  </button>
                </div>
              </div>
            </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render based on current step
  if (currentStep === 'story-setup') {
    return (
      <StorySetupModal
        isOpen={true}
        onClose={() => {
          const targetScene = Math.max(1, (currentScene || 1) - 1);
          if (storyId) {
            navigate(`/view-book/${storyId}?scene=${targetScene}`);
          } else {
            navigate('/storyboarding-landing');
          }
        }}
        onStorySetupComplete={handleStorySetupComplete}
      />
    );
  } else if (currentStep === 'conceptualization') {
    return (
      <>
        {renderConceptualizationStep()}
        {/* Scene/Object Library Modal - minimal placeholder for now */}
        {showSceneObjectLibrary && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
              <div className="p-4 border-b flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800">Scene/Object Library</h3>
                <button onClick={() => setShowSceneObjectLibrary(false)} className="text-gray-500 hover:text-gray-700 text-xl">×</button>
              </div>
              <div className="p-4 text-sm text-gray-700 max-h-[60vh] overflow-y-auto">
                {sceneObjectLibrary.length === 0 ? (
                  <p>No saved items yet. We’ll populate this with reusable scene items per story.</p>
                ) : (
                  <div className="space-y-2">
                    {sceneObjectLibrary.map(item => (
                      <div key={item.name} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <div className="font-semibold text-gray-800">{item.name}</div>
                          {item.description && <div className="text-xs text-gray-600">{item.description}</div>}
                          {item.sourceSceneNumber && <div className="text-xs text-gray-500">from Scene {item.sourceSceneNumber}</div>}
                        </div>
                        <button
                          onClick={() => handleInsertObjectFromLibrary(item)}
                          className="px-3 py-1 bg-amber-500 hover:bg-amber-600 text-white rounded-md text-sm"
                        >
                          Insert
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="p-4 border-t flex justify-end">
                <button onClick={() => setShowSceneObjectLibrary(false)} className="px-4 py-2 bg-amber-100 hover:bg-amber-200 text-amber-900 rounded-lg font-semibold">Close</button>
              </div>
            </div>
          </div>
        )}
        
        {/* Character Library Modal */}
        {showCharacterLibrary && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              <div className="bg-slate-600 p-6 text-white">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold">My Character Library</h2>
                  <button
                    onClick={() => setShowCharacterLibrary(false)}
                    className="text-white hover:text-gray-200 text-2xl"
                  >
                    ×
                  </button>
                </div>
              </div>
              
              <div className="p-6 max-h-[70vh] overflow-y-auto">
                <p className="text-sm text-gray-600 mb-4">Scroll horizontally to browse • Double-click to select a character</p>
                {userCharacters.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">📚</div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">No Characters Found</h3>
                    <p className="text-gray-600">You haven't created any characters yet. Create your first character to build your library!</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <div className="flex space-x-4 pb-4" style={{ minWidth: 'max-content' }}>
                      {userCharacters.map((character, index) => (
                        <div
                          key={character.id}
                          className="flex-shrink-0 w-32 cursor-pointer group relative"
                          onClick={() => handleCharacterSelectionFromLibrary(character)}
                          onDoubleClick={() => handleCharacterConfirmation()}
                        >

                          <div className="bg-white border border-gray-200 rounded-xl p-3 hover:border-slate-400 hover:shadow-lg transition-all duration-300 group-hover:scale-105">
                            {/* Delete Button - Always Visible */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                console.log('Delete button clicked for character:', character);
                                handleDeleteCharacterFromLibrary(character.id || character.characterId, character.name);
                              }}
                              className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold shadow-lg transition-all duration-200 z-50"
                              title={`Delete ${character.name}`}
                              style={{ opacity: 1 }}
                            >
                              <FaTrash className="text-xs" />
                            </button>
                            
                            <div className="w-full h-24 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden border border-gray-200 mb-2">
                              {character.imageUrl ? (
                                <img 
                                  src={character.imageUrl} 
                                  alt={character.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <FaUser className="text-gray-600 text-2xl" style={{ display: character.imageUrl ? 'none' : 'flex' }} />
                            </div>
                            <div className="text-center">
                              <div className="font-bold text-gray-800 text-sm truncate">{character.name}</div>
                              <div className="text-xs text-gray-600">{character.age} • {character.gender}</div>
                              <div className="text-xs text-gray-500 mt-1">{character.role || 'Character'}</div>
                              
                              {/* Subtle Delete Icon */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  console.log('Delete button clicked for character:', character);
                                  handleDeleteCharacterFromLibrary(character.id || character.characterId, character.name);
                                }}
                                className="absolute top-1 right-1 bg-red-400 hover:bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-60 hover:opacity-100 transition-all duration-200 z-10"
                                title={`Delete ${character.name}`}
                              >
                                <FaTrash className="text-xs" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Character Confirmation Display */}
        {characterConfirmed && selectedCharacterFromLibrary && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6">
              <div className="text-center mb-4">
                <h3 className="text-xl font-bold text-green-800 mb-2">Character Selected Successfully!</h3>
                <p className="text-green-600">This character has been added to your scene.</p>
              </div>
              
              <div className="flex items-center space-x-6 mb-6">
                {/* Character Image */}
                <div className="w-32 h-32 bg-green-100 rounded-lg flex items-center justify-center overflow-hidden border-2 border-green-200">
                  {selectedCharacterFromLibrary.imageUrl ? (
                    <img 
                      src={selectedCharacterFromLibrary.imageUrl} 
                      alt={selectedCharacterFromLibrary.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <FaUser className="text-green-600 text-4xl" style={{ display: selectedCharacterFromLibrary.imageUrl ? 'none' : 'flex' }} />
                </div>
                
                {/* Character Details */}
                <div className="flex-1">
                  <h5 className="text-2xl font-bold text-green-800 mb-2">{selectedCharacterFromLibrary.name}</h5>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><strong>Age:</strong> {selectedCharacterFromLibrary.age}</div>
                    <div><strong>Gender:</strong> {selectedCharacterFromLibrary.gender}</div>
                    <div><strong>Role:</strong> {selectedCharacterFromLibrary.role}</div>
                    <div><strong>Country:</strong> {selectedCharacterFromLibrary.countryOfOrigin || 'Unknown'}</div>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => {
                    handleCharacterConfirmation();
                    setShowAddAnotherCharacter(true);
                  }}
                  className="bg-amber-500 hover:bg-amber-600 text-white px-4 md:px-6 py-2.5 md:py-3 rounded-lg md:rounded-xl font-semibold transition-all duration-300 text-sm md:text-base"
                >
                  Add Another Character
                </button>
                <button
                  onClick={handleCharacterConfirmation}
                  className="bg-amber-500 hover:bg-amber-600 text-white px-4 md:px-6 py-2.5 md:py-3 rounded-lg md:rounded-xl font-semibold transition-all duration-300 text-sm md:text-base"
                >
                  Continue with Scene
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Character Modal */}
        {showEnhancedCharacterModal && (
          <EnhancedCharacterModal
            isOpen={showEnhancedCharacterModal}
            onClose={() => setShowEnhancedCharacterModal(false)}
            onCharacterAdded={handleEnhancedCharacterAdded}
            storyId={storySetupData?.storyId}
          />
        )}

        {/* Add Another Character Modal */}
        {showAddAnotherCharacter && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
              <div className="text-center">
                <div className="text-4xl mb-4">🎭</div>
                <h3 className="text-xl font-bold text-gray-800 mb-4">Character Added Successfully!</h3>
                
                {/* Show currently selected characters */}
                {sceneTags.length > 0 && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm font-semibold text-gray-800 mb-2">Currently Selected Characters ({sceneTags.length}):</p>
                    <div className="space-y-1">
                      {sceneTags.map((tag, index) => (
                        <div key={tag.id} className="text-xs text-gray-700 flex items-center justify-center">
                          <span className="w-4 h-4 bg-slate-500 rounded-full mr-2 flex-shrink-0"></span>
                          {tag.name} ({tag.role || 'Character'})
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <p className="text-gray-600 mb-6">Would you like to add another character to your story?</p>
                
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleAddAnotherCharacterDecision(true)}
                    className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-semibold py-2.5 md:py-3 px-4 md:px-6 rounded-lg md:rounded-xl shadow-md hover:shadow-lg transition-all duration-300 text-sm md:text-base"
                  >
                    Yes, Add Another
                  </button>
                  <button
                    onClick={() => handleAddAnotherCharacterDecision(false)}
                    className="flex-1 bg-amber-100 hover:bg-amber-200 text-amber-900 font-semibold py-2.5 md:py-3 px-4 md:px-6 rounded-lg md:rounded-xl border-2 border-amber-300 shadow-md hover:shadow-lg transition-all duration-300 text-sm md:text-base"
                  >
                    No, Continue
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Another Object Modal */}
        {showAddAnotherObject && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl">
              <div className="text-center">
                <div className="w-16 h-16 bg-slate-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <div className="w-8 h-8 bg-white rounded-lg transform rotate-45"></div>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-4">Scene Item Added Successfully!</h3>
                
                {/* Show currently added objects */}
                {objects.length > 0 && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm font-semibold text-gray-800 mb-2">Currently Added Scene Items ({objects.length}):</p>
                    <div className="space-y-1">
                      {objects.map((obj, index) => (
                        <div key={obj.id} className="text-xs text-gray-700 flex items-center justify-center">
                          <div className="w-3 h-3 bg-slate-500 rounded-sm mr-2 flex-shrink-0"></div>
                          {obj.name}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <p className="text-gray-600 mb-6">Would you like to add another scene item?</p>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleAddAnotherObjectDecision(false)}
                    className="flex-1 bg-amber-100 hover:bg-amber-200 text-amber-900 font-bold py-3 px-6 rounded-xl transition-colors"
                  >
                    Continue
                  </button>
                  <button
                    onClick={() => handleAddAnotherObjectDecision(true)}
                    className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
                  >
                    Add Another
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <UpgradeQuotaModal
          open={showUpgradeModal}
          onClose={dismissUpgradeModal}
          feature={upgradeModalFeature}
        />
      </>
    );
  } else if (currentStep === 'scene-view') {
    return (
      <>
        {renderSceneViewStep()}
        
        {/* Upload Artwork Modal - Only show on scene-view step */}
        {showUploadArtworkModal && (
          <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl md:rounded-2xl shadow-2xl max-w-md w-full mx-4 p-4 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg md:text-xl font-bold text-gray-800">Upload Your Artwork</h3>
                <button
                  onClick={() => {
                    setShowUploadArtworkModal(false);
                    if (artworkUploadRef.current) {
                      artworkUploadRef.current.value = '';
                    }
                  }}
                  className="text-gray-500 hover:text-gray-700 text-xl md:text-2xl"
                >
                  ×
                </button>
              </div>
              <p className="text-sm md:text-base text-gray-700 mb-4">
                The created image is an inspiration for you. Feel free to use the same for your book or upload your completed scene artwork.
              </p>
              <div className="mb-4">
                <input
                  ref={artworkUploadRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        const uploadedImageUrl = event.target.result;
                        setRefinedImageUrl(uploadedImageUrl);
                        
                        // Update the current scene in the scenes array with the uploaded artwork
                        setScenes(prevScenes => {
                          const existingSceneIndex = prevScenes.findIndex(scene => scene.sceneNumber === currentScene);
                          if (existingSceneIndex >= 0) {
                            // Update existing scene
                            const updatedScenes = [...prevScenes];
                            updatedScenes[existingSceneIndex] = {
                              ...updatedScenes[existingSceneIndex],
                              imageUrl: uploadedImageUrl,
                              refinedImageUrl: uploadedImageUrl
                            };
                            console.log('✅ Updated scene', currentScene, 'with uploaded artwork');
                            return updatedScenes;
                          } else {
                            // Create new scene entry if it doesn't exist
                            const newScene = {
                              id: `scene_${currentScene}_${Date.now()}`,
                              sceneNumber: currentScene,
                              imageUrl: uploadedImageUrl,
                              refinedImageUrl: uploadedImageUrl,
                              description: sceneDescription || `Scene ${currentScene}`,
                              characterDetails: characterDetails,
                              tags: sceneTags,
                              genre: sceneGenre,
                              mood: sceneMood,
                              createdAt: new Date().toISOString()
                            };
                            console.log('✅ Created new scene', currentScene, 'with uploaded artwork');
                            return [...prevScenes, newScene];
                          }
                        });
                        
                        setShowUploadArtworkModal(false);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                  className="hidden"
                  id="artwork-upload-input"
                />
                <label
                  htmlFor="artwork-upload-input"
                  className="block w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold py-2.5 md:py-3 px-4 md:px-6 rounded-lg md:rounded-xl shadow-md hover:shadow-lg transition-all duration-300 text-sm md:text-base text-center cursor-pointer"
                >
                  <FaUpload className="inline-block mr-2" />
                  Choose File
                </label>
              </div>
              <button
                onClick={() => {
                  setShowUploadArtworkModal(false);
                  if (artworkUploadRef.current) {
                    artworkUploadRef.current.value = '';
                  }
                }}
                className="w-full bg-amber-100 hover:bg-amber-200 text-amber-900 font-semibold py-2.5 md:py-3 px-4 md:px-6 rounded-lg md:rounded-xl transition-all duration-300 text-sm md:text-base"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        
        {/* Expanded Artwork Modal */}
        {console.log('=== EXPANDED ARTWORK MODAL DEBUG ===')}
        {console.log('isArtworkExpanded:', isArtworkExpanded)}
        {console.log('refinedImageUrl:', refinedImageUrl)}
        {isArtworkExpanded && refinedImageUrl && (
          <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setIsArtworkExpanded(false)}>
            <div className="relative w-full h-full max-w-7xl max-h-[95vh] flex items-center justify-center">
              <button
                onClick={() => setIsArtworkExpanded(false)}
                className="absolute top-6 right-6 bg-amber-500 hover:bg-amber-600 text-white font-semibold py-3 px-6 rounded-xl shadow-lg transition-colors z-10 text-lg"
              >
                × Close
              </button>
              <img 
                src={refinedImageUrl} 
                alt="Expanded Scene" 
                className="w-full h-full object-contain rounded-2xl shadow-2xl max-w-full max-h-full"
              />
            </div>
          </div>
        )}

        {/* Scene Edit Modal */}
        {console.log('=== MODAL RENDERING DEBUG ===')}
        {console.log('showSceneEditModal:', showSceneEditModal)}
        {console.log('currentSceneForEdit:', currentSceneForEdit)}
        {showSceneEditModal && currentSceneForEdit && (
          <SceneEditModal
            isOpen={showSceneEditModal}
            onClose={() => setShowSceneEditModal(false)}
            scene={currentSceneForEdit}
            onSceneUpdate={handleSceneUpdate}
            onUndo={handleSceneUndo}
          />
        )}

        {/* Other modals */}
        {showCropper && (
          <StoryboardingCropper
            imageUrl={refinedImageUrl}
            tags={sceneTags}
            onSave={(savedProfiles) => {
              console.log('Character profiles saved:', savedProfiles);
              setShowCropper(false);
              alert('Character profiles saved successfully!');
            }}
            onCancel={() => setShowCropper(false)}
          />
        )}
        {showAddCharacterModal && (
          <AddCharacterModal
            isOpen={showAddCharacterModal}
            onClose={() => setShowAddCharacterModal(false)}
            onCharacterAdded={handleAddNewCharacter}
          />
        )}
        {showCharacterPreviewModal && currentCharacterForPreview && (
          <CharacterPreviewModal
            isOpen={showCharacterPreviewModal}
            onClose={() => setShowCharacterPreviewModal(false)}
            character={currentCharacterForPreview}
            characterImage={currentCharacterImage}
            onSaveCharacter={handleCharacterPreviewSave}
            onEditCharacter={handleCharacterEdit}
            onRegenerateImage={handleCharacterRegenerate}
          />
        )}

        {/* Delete Confirmation Modal */}
        <CustomModal
          isOpen={showDeleteConfirmation}
          onClose={() => {
            setShowDeleteConfirmation(false);
            setCharacterToDelete(null);
          }}
          onConfirm={confirmDeleteCharacter}
          title={
            characterToDelete?.success 
              ? "Character Deleted Successfully!" 
              : characterToDelete?.error 
                ? "Delete Failed" 
                : "Delete Character"
          }
          message={
            characterToDelete?.success 
              ? `"${characterToDelete.name}" has been successfully removed from your character library.`
              : characterToDelete?.error 
                ? `Failed to delete "${characterToDelete.name}". Please try again.`
                : `Are you sure you want to delete "${characterToDelete?.name}" from your character library? This action cannot be undone.`
          }
          confirmText={
            characterToDelete?.success || characterToDelete?.error 
              ? "OK" 
              : "Delete"
          }
          cancelText={
            characterToDelete?.success || characterToDelete?.error 
              ? null 
              : "Cancel"
          }
          type={
            characterToDelete?.success 
              ? "success" 
              : characterToDelete?.error 
                ? "error" 
                : "warning"
          }
        />

        {/* Save Success Modal */}
        <CustomModal
          isOpen={showSaveSuccessModal}
          onClose={() => {
            setShowSaveSuccessModal(false);
            // Do NOT navigate - stay on current page/step
          }}
          title="Story Saved Successfully!"
          message="Your story has been saved to My Art Stories. Keep creating!"
          type="success"
          confirmText="OK"
        />

        {/* Content Moderation Modal */}
        <ContentModerationModal
          isOpen={showContentModerationModal}
          onClose={() => setShowContentModerationModal(false)}
          message={contentModerationMessage}
          matchedKeywords={contentModerationKeywords}
        />

        <UpgradeQuotaModal
          open={showUpgradeModal}
          onClose={dismissUpgradeModal}
          feature={upgradeModalFeature}
        />
      </>
    );
  }

  return null;
}
